import { sql } from "drizzle-orm";
import type { SavedFilter } from "./notification-preferences";
import { pgTable, text, varchar, boolean, integer, jsonb, timestamp, index, uniqueIndex, uuid, primaryKey } from "drizzle-orm/pg-core";
import { users } from "./models/auth";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";
import type { BagDetectionTrace } from "./bag-detection-trace";
import type { YoloPredictionsTrace } from "./yolo-predictions-trace";

// Export auth schema for Replit Auth integration
export * from "./models/auth";

// SPEC-Auth-Users-Roles-Audit — the canonical `users` table is exported from
// `./models/auth` via the `export * from "./models/auth"` line above. The
// previous `approvedEmails` table + `users` (OIDC-bookkeeping) table were
// consolidated into a single source of truth. See:
//   ~/Productivity/QAM/foundation/SPEC-Auth-Users-Roles-Audit.md
//
// Migration is run idempotently at boot from `server/index.ts` (see the
// foundation canonical-users migration block) — the SQL file lives at
// `migrations/2026-05-28-foundation-canonical-users.sql` for the Sarah-owned
// records / rollback path.

// Zod schemas for the admin /api/users endpoints. Role validation matches
// the DB CHECK constraint (`isValidRole` in `./models/auth`).
import { ROLE_VALUES } from "./models/auth";

// SPEC-UserProvisioning §2 — initials format matches the DB CHECK constraint
// (`users_initials_format`) added by migrations/2026-09-02-user-provisioning.sql.
const initialsSchema = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[A-Z0-9]{2,3}$/, "Initials must be 2–3 letters");

export const insertUserSchema = z.object({
  email: z.string().email().transform((s) => s.toLowerCase().trim()),
  name: z.string().min(1, "Name is required"),
  role: z.enum(ROLE_VALUES),
  isAdmin: z.boolean().optional().default(false),
  initials: initialsSchema,
});
export type InsertUser = z.infer<typeof insertUserSchema>;

// PATCH /api/users/:id body — all fields optional. Role validated when present.
export const patchUserSchema = z.object({
  name: z.string().min(1).optional(),
  role: z.enum(ROLE_VALUES).optional(),
  isAdmin: z.boolean().optional(),
  initials: initialsSchema.optional(),
  isActive: z.boolean().optional(),
  // Per-user QATT scope. `null` clears the restriction (unrestricted); an
  // array must be non-empty, unique, values 1–4 (TASK_CATEGORIES). Matches
  // the DB CHECK `users_allowed_qatts_valid`.
  allowedQatts: z
    .array(z.number().int().min(1).max(4))
    .min(1, "Pick at least one task type, or clear the restriction")
    .refine((a) => new Set(a).size === a.length, "Duplicate task type")
    .nullable()
    .optional(),
});
export type PatchUser = z.infer<typeof patchUserSchema>;

// Task category types for different QA criteria
// Type 1: Dropoff - Connect (CookUnity) - organization !== executor
// Type 2: Dropoff - B2B - organization === executor AND customFields has "BUSINESS to BUSINESS"
// Type 3: Dropoff - Regular - organization === executor AND no B2B in customFields
// Type 4: Pickup - type = 0
export const TASK_CATEGORIES = {
  CONNECT: 1,
  B2B: 2,
  REGULAR: 3,
  PICKUP: 4,
} as const;

export type TaskCategory = typeof TASK_CATEGORIES[keyof typeof TASK_CATEGORIES];

export const TASK_CATEGORY_LABELS: Record<TaskCategory, string> = {
  1: "Dropoff - Connect",
  2: "Dropoff - B2B",
  3: "Dropoff - Regular",
  4: "Pickup",
};

// Helper function to compute task category from task data
export function getTaskCategory(taskData: unknown): TaskCategory {
  if (!taskData || typeof taskData !== 'object') return TASK_CATEGORIES.REGULAR;
  
  const data = taskData as {
    type?: number;
    organization?: string;
    executor?: string;
    customFields?: Array<{ key?: string; value?: string }>;
  };
  
  // Pickup: type = 0
  if (data.type === 0) {
    return TASK_CATEGORIES.PICKUP;
  }
  
  // Connect: organization !== executor
  if (data.organization && data.executor && data.organization !== data.executor) {
    return TASK_CATEGORIES.CONNECT;
  }
  
  // B2B: organization === executor AND customFields contains "BUSINESS to BUSINESS"
  // Note: B2B requires org === executor per spec, so Connect check above filters these out
  if (data.customFields && Array.isArray(data.customFields)) {
    const hasB2B = data.customFields.some(
      field => field.key === 'type' && 
        field.value?.toUpperCase().includes('BUSINESS TO BUSINESS')
    );
    if (hasB2B) return TASK_CATEGORIES.B2B;
  }
  
  // Default: Regular dropoff
  return TASK_CATEGORIES.REGULAR;
}

// Helper function to check if signature is required from task data
export function isSignatureRequired(taskData: unknown): boolean {
  if (!taskData || typeof taskData !== 'object') return false;
  
  const data = taskData as {
    requirements?: { signature?: boolean };
  };
  
  return data.requirements?.signature === true;
}

// Drop zone codes — intent-anchored, address-type-agnostic.
// DOOR is the universal default: house front door, unit door, secondary unit door — all equivalent.
// Specific codes apply when the recipient gave an explicit instruction or the driver deviated.
export const DROP_ZONE_CODES = [
  "DOOR",           // Resident's main entrance — house front door or unit door (default)
  "OTHER_ENTRANCE", // Any non-default entrance: side, rear, back door, garage, basement
  "INTERIOR_PORCH", // Inside porch, vestibule, or storm door
  "FRONT_DESK",     // Building common area: concierge, doorman, lobby, security desk
  "STORAGE",        // Designated secure area: mailroom, parcel locker, shelf, parcel room
  "H2H",            // Hand to hand — direct recipient handoff
  "UNK",            // Undetermined / conflicting instructions
  "UNSAFE",         // ⚠ Bag left in exposed public area — no customer ever requests this; triggers immediate alert
] as const;

export type DropZoneCode = typeof DROP_ZONE_CODES[number];

// Customers table - stores customer profiles
export const customers = pgTable("customers", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull(),
  address: text("address").notNull(),
  dropZone: text("drop_zone").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

// Characteristics - stores AI-identified and user-validated characteristics
export const characteristics = pgTable("characteristics", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  customerId: varchar("customer_id").notNull().references(() => customers.id, { onDelete: "cascade" }),
  type: text("type").notNull(), // 'address' or 'dropzone'
  description: text("description").notNull(),
  isRelevant: boolean("is_relevant").default(true),
  source: text("source").notNull(), // 'ai' or 'user'
});

// Training photos - stores verified training images
export const trainingPhotos = pgTable("training_photos", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  customerId: varchar("customer_id").notNull().references(() => customers.id, { onDelete: "cascade" }),
  imageUrl: text("image_url").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

// QA Workflow Status - operational state of the task in the QA process
export const STARLING_QA_STATUS = [
  "Waiting",           // Completed task not yet started by an Agent
  "In Progress",       // Agent actively reviewing; no decision submitted yet
  "Needs Resolution",  // Decision submitted as Bad or Unclear; follow-up required
  "Resolved",          // QA is complete and final decision is Good
  "Skipped"            // Task intentionally not QA'd
] as const;

export type StarlingQAStatus = typeof STARLING_QA_STATUS[number];

// QA Decision - outcome label for delivery correctness
export const QA_DECISIONS = [
  "Good",    // Expected matches Actual with sufficient evidence
  "Bad",     // Clear mismatch between Expected and Actual
  "Unclear"  // No evidence of Bad, but insufficient evidence to confirm Good
] as const;

export type QaDecision = typeof QA_DECISIONS[number];

// Error types for QA failures
export const ERROR_TYPES = [
  "order_item",          // Order item issue (wrong item, damaged, missing)
  "recipient",           // Recipient issue (wrong person, no signature)
  "address",             // Address issue (wrong address)
  "drop_zone",           // Drop zone issue (wrong drop zone)
  "other_requirements",  // Other requirements issue
  "unverifiable_pod"     // Insufficient/poor POD evidence to verify
] as const;

export type ErrorType = typeof ERROR_TYPES[number];

// Region Review Status — for SPEC-annotation-review-mode.
// Sarah uses Annotation Review Mode to walk the 1,658 existing cookunity_bag
// bbox annotations: 'pending' is the migration default, 'verified' marks an
// annotation accepted (with or without edits) for retraining, 'discarded'
// removes it from the YOLO export seed set.
export const REGION_REVIEW_STATUSES = ["pending", "verified", "discarded"] as const;
export type RegionReviewStatus = typeof REGION_REVIEW_STATUSES[number];

// QA Deliveries - stores Onfleet tasks for QA workflow
export const qaDeliveries = pgTable("qa_deliveries", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  onfleetTaskId: varchar("onfleet_task_id").notNull().unique(),
  onfleetState: integer("onfleet_state").notNull(), // 0=Unassigned, 1=Assigned, 2=Active, 3=Completed
  starlingStatus: text("starling_status").notNull().default("Waiting"), // Waiting, In Progress, Needs Resolution, Resolved, Skipped
  qaDecision: text("qa_decision"), // GOOD, BAD, UNCLEAR (null = no decision yet). Legacy shared column — the role-scoped agent_/senior_ decision + resolution columns are authoritative.
  qaDecisionLockedAt: timestamp("qa_decision_locked_at"), // Set when the delivery enters its terminal state (GOOD, including GOOD-after-resolve).
  cuNotifiedAt: timestamp("cu_notified_at"), // SPEC-AgentUI-Backend: orthogonal CU-notify flag (label adapts per Customer).
  cuNotifiedBy: uuid("cu_notified_by").references((): any => users.id), // SPEC-AgentUI-Backend: the user who ticked Notify Customer.
  // SPEC-AgentList-Hardening — "sent" state for the CU-pending workflow.
  // Distinct from cuNotifiedAt (the "should be notified" flag): cuNotificationSentAt
  // captures the explicit "I sent the Slack" confirmation. NeedsResolution's
  // cu-pending variant drops a row off the list once this is set. Idempotent
  // first-write-wins via the endpoint's `isNull(cuNotificationSentAt)` filter.
  cuNotificationSentAt: timestamp("cu_notification_sent_at", { withTimezone: true }),
  cuNotificationSentBy: uuid("cu_notification_sent_by").references((): any => users.id),
  deliveryDate: timestamp("delivery_date").notNull(), // The date this delivery is scheduled for
  completedAt: timestamp("completed_at"), // When the task was completed in Onfleet
  recipientName: text("recipient_name"),
  recipientPhone: text("recipient_phone"),
  address: text("address"),
  notes: text("notes"),
  destinationNotes: text("destination_notes"),
  photoUrls: text("photo_urls").array(), // Completion photos from Onfleet
  workerName: text("worker_name"),
  workerPhone: text("worker_phone"),
  shortId: varchar("short_id"), // Onfleet short ID for easy reference
  trackingUrl: text("tracking_url"),
  taskData: jsonb("task_data"), // Full Onfleet task JSON for reference
  // QA Verification fields - criteria per task type:
  // Type 1 (Connect): Address, Drop Zone, Bag Notes
  // Type 2 (B2B): Items, Address, Signature (if required)
  // Type 3 (Regular): Items, Address, Drop Zone
  // Type 4 (Pickup): Items, Quantity
  qaAddressCorrect: boolean("qa_address_correct"), // Types 1, 2, 3: Address verification
  qaBagNotesCorrect: boolean("qa_bag_notes_correct"), // Type 1 (Connect): Bag notes verification
  qaItemsCorrect: boolean("qa_items_correct"), // Types 2, 3, 4: Items verification
  qaQuantityCorrect: boolean("qa_quantity_correct"), // Type 4 (Pickup): Quantity verification
  qaSignatureCorrect: boolean("qa_signature_correct"), // Type 2 (B2B): Signature verification (if required)
  qaModifiedCriteria: jsonb("qa_modified_criteria").$type<string[]>(), // Legacy: now used for override criteria (criteria where user overrode expected values)
  qaOverrideNotes: jsonb("qa_override_notes").$type<Record<string, string>>(), // Override explanations per criterion (e.g. {"address": "Customer note says leave at 132"})
  qaOverrideEvidenceRefs: jsonb("qa_override_evidence_refs").$type<Record<string, string[]>>(), // Evidence source refs per override criterion (e.g. {"address": ["photo-0", "completion-notes"]})
  qaInferredCriteria: jsonb("qa_inferred_criteria").$type<string[]>(), // Criteria verified without evidence annotations (human inference)
  agentActualEvidenceReviewed: jsonb("agent_actual_evidence_reviewed").$type<Array<{
    dataSourceKey: string;
    criterionKey: string;
    signal: "verifies" | "inconclusive" | "error";
    photoRef?: string;
    domain?: "order" | "location" | "requirements";
    verdict?: "supports" | "contradicts" | "unclear";
    noteId?: string;
    createdAt?: string;
    evidenceKey?: string;
    notes?: string;
  }>>(),
  agentRightLocationChecklist: jsonb("agent_right_location_checklist").$type<{ streetNumberVisible: boolean; unitNumberVisible: boolean; dropZoneIdentifiable: boolean; exclusiveEvidencePresent: boolean }>(),
  deliveryConfidenceScore: integer("delivery_confidence_score"), // 0-100 computed from criteria states at verify time
  autoGoodCandidate: boolean("auto_good_candidate"), // true if delivery qualifies for auto-good (score>=90, no overrides, no mismatches)
  evidenceCoverageScore: integer("evidence_coverage_score"), // 0-100 percentage of correct/override criteria with evidence signals
  // Legacy fields - kept for backward compatibility with existing data
  qaOtherInstructionsFollowed: boolean("qa_other_instructions_followed"), // LEGACY - deprecated
  qaReceiverCorrect: boolean("qa_receiver_correct"), // LEGACY - deprecated
  qaPackagingCorrect: boolean("qa_packaging_correct"), // LEGACY - deprecated
  // AI Prediction fields (populated when PDR reference exists)
  aiAddressCorrect: boolean("ai_address_correct"), // AI prediction: is the address correct?
  aiAddressConfidence: integer("ai_address_confidence"), // 0-100 confidence score
  // AI Items/Packaging prediction (Type 2/3 - from Company PDR)
  aiItemsCorrect: boolean("ai_items_correct"), // AI prediction: are items/packaging correct?
  aiItemsConfidence: integer("ai_items_confidence"), // 0-100 confidence score
  // AI Label/Recipient prediction (Type 2/3 - from Company PDR)
  aiLabelRecipientCorrect: boolean("ai_label_recipient_correct"), // AI prediction: does label match expected recipient?
  aiLabelRecipientConfidence: integer("ai_label_recipient_confidence"), // 0-100 confidence score
  aiPredictionMadeAt: timestamp("ai_prediction_made_at"), // When the AI prediction was made
  // Enhanced AI signals - Location prediction (from PDR)
  aiOcrTextsFound: jsonb("ai_ocr_texts_found").$type<string[]>(),
  aiOcrAddressMatch: boolean("ai_ocr_address_match"),
  aiOcrLabelMatch: boolean("ai_ocr_label_match"),
  aiVisualSimilarity: integer("ai_visual_similarity"),
  aiObjectsDetected: jsonb("ai_objects_detected").$type<string[]>(),
  aiObjectsMissing: jsonb("ai_objects_missing").$type<string[]>(),
  aiImageQuality: integer("ai_image_quality"),
  aiImageQualityFlags: jsonb("ai_image_quality_flags").$type<string[]>(),
  // PDR Anomaly Detection - flagged when new POD has low visual similarity to previously verified POD
  pdrAnomalyDetected: boolean("pdr_anomaly_detected"),
  // Enhanced AI signals - Company prediction (from Company PDR)
  aiCompanyOcrTextsFound: jsonb("ai_company_ocr_texts_found").$type<string[]>(),
  aiCompanyOcrLabelMatch: boolean("ai_company_ocr_label_match"),
  aiCompanyVisualSimilarity: integer("ai_company_visual_similarity"),
  aiCompanyObjectsDetected: jsonb("ai_company_objects_detected").$type<string[]>(),
  aiCompanyObjectsMissing: jsonb("ai_company_objects_missing").$type<string[]>(),
  aiCompanyImageQuality: integer("ai_company_image_quality"),
  aiCompanyImageQualityFlags: jsonb("ai_company_image_quality_flags").$type<string[]>(),
  // Error/Unclear tracking fields
  errorType: text("error_type"), // Type of error: address, dropzone, damaged, wrong_item, other
  resolutionNotes: text("resolution_notes"), // How the issue was resolved (legacy single note)
  resolutionItems: jsonb("resolution_items"), // Array of {issueType, resolutionMethod, notes} for multi-issue resolution
  resolvedAt: timestamp("resolved_at"), // When the issue was resolved
  resolvedBy: varchar("resolved_by"), // User ID who resolved the issue
  resolvedByName: text("resolved_by_name"), // User name who resolved the issue
  // Owner tracking - user who opened this delivery for QA
  ownerId: varchar("owner_id"), // User ID of the QA owner
  ownerName: text("owner_name"),
  ownerClaimedAt: timestamp("owner_claimed_at"), // When the user claimed this delivery
  // Alert tracking - for Slack notifications
  lastAlertSentAt: timestamp("last_alert_sent_at"), // When the last alert was sent for this delivery
  alertCount: integer("alert_count").default(0), // Number of alerts sent for this delivery
  source: text("source").default("sync"), // "sync" = regular webhook/sync, "pdr_import" = imported from past delivery discovery, "cu_baseline" = competitor baseline queue
  qaRequirement: text("qa_requirement"), // Custom QA note/requirement added by admin for this specific order
  qaRequirementAddedBy: text("qa_requirement_added_by"), // Who added the QA requirement
  qaRequirementAddedAt: timestamp("qa_requirement_added_at"), // When the QA requirement was added
  qaRequirementAcknowledged: boolean("qa_requirement_acknowledged").default(false), // Whether the QA reviewer acknowledged the requirement
  qaRequirementAcknowledgedBy: text("qa_requirement_acknowledged_by"), // Who acknowledged the requirement
  qaRequirementAcknowledgedAt: timestamp("qa_requirement_acknowledged_at"), // When the requirement was acknowledged
  extraPhotoUrls: text("extra_photo_urls").array(), // Extra driver photos uploaded outside Onfleet (stored as server file paths)
  // Resolution audit trail fields (Milestone C)
  qaDecisionInitial: text("qa_decision_initial"), // First QA decision made (Good/Bad/Unclear), never overwritten
  qaDecisionFinal: text("qa_decision_final"), // Most recent QA decision after resolution cycles
  resolutionCount: integer("resolution_count").default(0).notNull(), // Number of resolution cycles completed
  // Domain-level decisions — one per QA domain, values: Good | Bad | Unclear | null
  qaOrderDecision: text("qa_order_decision"), // Order domain: items, packaging, labeling, recipient
  qaLocationDecision: text("qa_location_decision"), // Location domain: address, drop zone
  qaRequirementsDecision: text("qa_requirements_decision"), // Requirements domain: completion reqs, instructions
  // Structured evidence reviewed by agent (entries with per-item review verdicts)
  qaEvidenceReviewed: jsonb("qa_evidence_reviewed").$type<Array<{ sourceKey: string; verdict: "helpful" | "not_helpful" | "inconclusive"; criterionKey?: string; notes?: string }>>(),
  // Structured agent overrides (field corrections with reason and evidence refs)
  qaOverrides: jsonb("qa_overrides").$type<Record<string, { field: string; value: string; side: string; reason?: string; evidenceRefs?: string[] }>>(),
  qaRegionAnnotations: jsonb("qa_region_annotations"),
  // SPEC-annotation-review-mode (Phase 1) — review queue for the existing
  // 1,658 cookunity_bag bbox annotations. These three fields are written
  // ONLY by PATCH /api/qa-deliveries/:id/region-annotations and never by
  // the QA submit flow. Status enum is enforced by a CHECK constraint
  // applied alongside the column add in server/index.ts startup migration.
  regionReviewStatus: text("region_review_status").notNull().default("pending"), // pending | verified | discarded
  regionReviewedAt: timestamp("region_reviewed_at"), // set when status leaves 'pending'
  regionReviewedBy: text("region_reviewed_by"), // user identifier (email) of reviewer
  // SPEC-annotation-review-concurrency (Phase 1) — soft-claim namespace for
  // the annotation-review queue. Mirrors the agent-QA-flow's ownerId/
  // ownerName/ownerClaimedAt triple but lives in its own column namespace:
  // the QA flow and the annotation review flow coexist on the same row and
  // do not read each other's claim columns. A claim is considered ACTIVE
  // iff claimer_id IS NOT NULL AND claimed_at > now() - REVIEW_CLAIM_TTL_MS
  // (see shared/annotation-review-claims.ts). All three are written ONLY by
  // the websocket review:claim/release handlers and the version-checked
  // PATCH endpoint, never by the QA submit flow.
  regionReviewClaimerId: text("region_review_claimer_id"), // userId (email) of the reviewer currently in review mode on this row
  regionReviewClaimerName: text("region_review_claimer_name"), // display name of the claimer ("Sarah is reviewing")
  regionReviewClaimedAt: timestamp("region_review_claimed_at"), // wall-clock time claim was opened or last refreshed
  // SPEC-annotation-review-seed-sources Phase 1 — stable per-batch tag
  // identifying the recruitment query that surfaced this delivery into the
  // review queue. NULL on rows that pre-date the spec OR rows never
  // recruited. Written ONLY by script/seed-review-batch.mjs and the boot-
  // time backfill in server/index.ts; never touched by the PATCH handler
  // or the QA submit flow. The queue/neighbors handlers expose a
  // ?seed_source=<name> URL filter that joins on this column.
  regionReviewSeedSource: text("region_review_seed_source"),
  goodOverrideNote: text("good_override_note"),
  // Expected Extraction Model predictions (PRD Section 8-9)
  // Stored at sync time from task data - agents validate during QA
  expectedRecipient: text("expected_recipient"), // PRD 8.2: Extracted recipient name
  expectedStreetNumber: text("expected_street_number"), // PRD 8.3: Parsed street number
  expectedUnitNumber: text("expected_unit_number"), // PRD 8.4: Parsed unit/apt number
  expectedDzeCode: text("expected_dze_code"), // PRD 9: Expected drop zone code (DropZoneCode)
  expectedQatt: integer("expected_qatt"), // PRD 5: QA Task Type (1-4)
  expectedOtherCompletionRequirements: jsonb("expected_other_completion_requirements").$type<string[]>(), // PRD 10: canonical completion requirement tokens
  expectedModelBasis: jsonb("expected_model_basis").$type<Record<string, string>>(),
  expectedExtractedAt: timestamp("expected_extracted_at"), // When extraction ran
  // Actual Extraction Model predictions (from POD / completion data)
  // Extracted post-completion from driver notes, signature, and AI vision
  actualRecipient: text("actual_recipient"), // Observed recipient identity (from invoice/label OCR, task recipients — NOT signature)
  actualSignatureName: text("actual_signature_name"), // Name written on signature pad (from signatureText)
  actualStreetNumber: text("actual_street_number"), // Observed street number (from photos via AI vision)
  actualUnitNumber: text("actual_unit_number"), // Observed unit number (from photos via AI vision)
  actualDzeCode: text("actual_dze_code"), // Observed drop zone code (DropZoneCode)
  actualOtherCompletionRequirements: jsonb("actual_other_completion_requirements").$type<string[]>(), // PRD 10: canonical observed completion requirement tokens
  podAttachments: jsonb("pod_attachments").$type<Array<{ id: string; type: "photo" | "signature"; url?: string }>>(), // Structured list of Onfleet completionDetails attachments (photos + signature)
  actualEvidence: jsonb("actual_evidence"), // Array of {field, evidenceType, description, photoRef?, confidence?}
  ocrAddressMatch: boolean("ocr_address_match"), // Baseline OCR: does photo text match expected address?
  ocrAddressConfidence: integer("ocr_address_confidence"), // 0-100 OCR address match confidence
  ocrTextsFound: jsonb("ocr_texts_found").$type<string[]>(), // Short list of OCR-extracted address tokens
  ocrEntities: jsonb("ocr_entities").$type<Array<{ field: string; value: string; confidence: number; photoRef: string; source: string }>>(), // OCR-extracted entities from POD photos (recipient, address, items)
  // OCR_ROUTING_V2 (SPEC-qam-conditional-ocr): for QATT_1 deliveries, true when the
  // CookUnity bag was definitively NOT detected in any photo and we therefore skipped
  // OCR. The QA UI surfaces "no bag visible — check with driver" in this state.
  // Null = not evaluated (V2 routing off, non-QATT_1, or bag was detected).
  podMissingBag: boolean("pod_missing_bag"),
  actualExtractionStatus: text("actual_extraction_status").default("pending"), // pending | complete | error
  actualExtractedAt: timestamp("actual_extracted_at"), // When actual extraction ran
  actualExtractionErrorReason: text("actual_extraction_error_reason"), // Error details when actualExtractionStatus = error
  aiRightItemSuggestion: text("ai_right_item_suggestion"), // AI vision: yes | no | unclear
  aiRightItemConfidence: integer("ai_right_item_confidence"), // 0-100 confidence score
  aiRightItemEvidence: jsonb("ai_right_item_evidence").$type<string[]>(), // Short list of reasons
  aiRightItemExtractedAt: timestamp("ai_right_item_extracted_at"), // When AI item detection ran
  locationCoherenceScore: integer("location_coherence_score"), // 0-100 heuristic coherence between address anchor and drop zone photos
  locationCoherenceNotes: text("location_coherence_notes"), // Explanation of coherence computation
  regionComparisonResult: jsonb("region_comparison_result"), // AI visual comparison of marked PDR baseline regions vs new delivery photos
  sceneClassifierTrace: jsonb("scene_classifier_trace"), // Explainability trace for scene classifier decisions (addressType, dzeCode)
  // SPEC-model-v2-step1-bag-routing — per-photo audit trail emitted by
  // runConditionalOcrV2 when BAG_ROUTING_YOLO=true on a QATT_1 delivery.
  // Null when the flag is off, when the delivery is non-QATT_1, or when YOLO
  // crashed and we degraded to today's path. See shared/bag-detection-trace.ts
  // for the type definition + per-field rationale.
  bagDetectionTrace: jsonb("bag_detection_trace").$type<BagDetectionTrace>(),
  // SPEC-yolo-multiclass-prediction-overlay — per-delivery record of every
  // YOLO detection emitted by ONNX inference at conf ≥ L1 (CONF_THRESHOLD =
  // 0.20), grouped by photo. Pre-suppression and pre-OCR. Independent of
  // `bag_detection_trace`: that column records the routing DECISION on a
  // QATT_1 delivery under BAG_ROUTING_YOLO=true; this column records what
  // the model SAW on every YOLO run. See shared/yolo-predictions-trace.ts
  // for the type definition + per-field rationale. Null when YOLO did not
  // run (env disabled, no photos, or YOLO crashed entirely for the delivery).
  yoloPredictions: jsonb("yolo_predictions").$type<YoloPredictionsTrace>(),
  actualExtractionVersion: text("actual_extraction_version"), // "v1" | "v2" — which extraction pipeline produced these results
  lastEvidenceHash: text("last_evidence_hash"), // SHA-256 of completionDetails evidence inputs for idempotent extraction
  matchedPdrReferenceId: varchar("matched_pdr_reference_id"), // PDR reference matched at sync/load time (company+recipient+street)
  // Scene fingerprint — structured visual description of the delivery location, generated by gpt-4o-mini from the best qualifying photo
  sceneFingerprint: jsonb("scene_fingerprint"),
  // Scene comparison vs PDR reference fingerprint (0-100 score, null when no PDR fingerprint exists yet)
  sceneMatchScore: integer("scene_match_score"),
  sceneMatchDetails: jsonb("scene_match_details").$type<Record<string, boolean | null>>(),
  baselineReviewDecision: jsonb("baseline_review_decision").$type<{
    overall: "match" | "possible_match" | "no_match";
    reviewerId: string;
    reviewerName: string;
    reviewedAt: string;
    notes?: string;
  }>(),
  systemStatus: text("system_status").notNull().default("ok"), // ok | error — tracks system processing health
  systemErrorReason: text("system_error_reason"), // Description of system failure (null when systemStatus = ok)
  // SPEC-PreScreening Phase 0 (WI-1) — prescreen lifecycle marker for the
  // state=1 (assigned) pre-delivery review flow. Default 'not_prescreened' so
  // every existing row and every sync-created row is well-defined without a
  // backfill. Set to 'prescreened' by the Phase 1 mark-prescreened action;
  // preserved across re-import and completion by the merge contract in
  // upsertQaDelivery (WI-2) so a prescreened draft is never dragged back to
  // 'not_prescreened'. Drizzle column mirrored by an idempotent ADD COLUMN IF
  // NOT EXISTS migration in server/index.ts (Drizzle does not apply migrations
  // on its own here).
  prescreenStatus: text("prescreen_status").default("not_prescreened"), // 'not_prescreened' | 'prescreened'
  prescreenedAt: timestamp("prescreened_at"), // set when status becomes 'prescreened'
  prescreenedBy: text("prescreened_by"), // user id/name who marked it prescreened
  // SPEC-PreScreening Phase 2 (WI-1) — human-authored, written ONLY by the
  // prescreen flow, never by sync/extraction. Both are pinned from the existing
  // row in the upsertQaDelivery preserve block (WI-2) so completion (state 1→3)
  // and re-import never clobber them.
  // prescreenFlags: advisory validation flags (Decision B) — surfaced as a
  // full-width banner at completion, no check-off.
  prescreenFlags: jsonb("prescreen_flags").$type<PrescreenFlag[]>(),
  // prescreenCorrectedFields: per editable expected field, the model's original
  // prediction + the human correction (Decision A). Retains the raw prediction
  // that the re-derived expected* columns lose at completion.
  prescreenCorrectedFields: jsonb("prescreen_corrected_fields").$type<PrescreenCorrectedFields>(),
  // SPEC-qa-detail-v3-flags-notes — two delivery-scoped, QA-side jsonb columns
  // for the post-delivery QA view. Both human-authored by the QA flow ONLY
  // (never by sync/extraction); pinned in the upsertQaDelivery preserve block
  // (same contract as prescreenFlags) so completion (state 1→3) and re-import
  // never clobber them. Null = "nothing yet" (no backfill). Drizzle columns
  // mirrored by an idempotent ADD COLUMN IF NOT EXISTS in server/index.ts.
  // qaFlags = validation alerts (open/resolved); qaNotes = free-form reference
  // log. See QaFlag / QaNote above for the shapes.
  qaFlags: jsonb("qa_flags").$type<QaFlag[]>(),
  qaNotes: jsonb("qa_notes").$type<QaNote[]>(),
  // SPEC-ActiveTask Phase B — CookUnity early-delivery tracking. All nullable,
  // no backfill (null = "not evaluated"). completedEarly/earlyByMs are set by
  // the B0 retrospective compute on the taskCompleted webhook for QATT_1 tasks
  // (completionDetails.time < completeAfter). earlyRiskAlertedAt is the B1
  // dedupe latch — set when the predictive early-risk alert fires on
  // taskStarted, so re-receiving the event does not double-alert. Drizzle
  // columns mirrored by an idempotent ADD COLUMN IF NOT EXISTS in server/index.ts.
  completedEarly: boolean("completed_early"),
  earlyByMs: integer("early_by_ms"),
  earlyRiskAlertedAt: timestamp("early_risk_alerted_at"),
  // SPEC-qa-actions-audit-log — additive FK columns alongside the existing
  // text columns (owner_name / resolved_by_name / prescreened_by /
  // region_review_claimer_name). Forward-only: new writes populate both the
  // FK and the existing text column; historical rows have NULL FKs and
  // display via the text fallback. A later cleanup SPEC drops the text
  // columns once the FKs prove out. Mirrored by an idempotent ADD COLUMN IF
  // NOT EXISTS block in server/index.ts and by the standalone migration at
  // migrations/2026-05-28-qa-actions-and-fk-columns.sql.
  ownerUserId: uuid("owner_user_id").references((): any => users.id),
  resolvedByUserId: uuid("resolved_by_user_id").references((): any => users.id),
  prescreenedByUserId: uuid("prescreened_by_user_id").references((): any => users.id),
  regionReviewClaimerUserId: uuid("region_review_claimer_user_id").references((): any => users.id),
  // SPEC-EffectiveDecision — Delivery State Model role-scoped decision columns.
  // Migration: migrations/2026-06-05-effective-decision.sql
  // Boot-time idempotent block: server/index.ts
  //
  // agent_decision / agent_resolution — set by agent (and bot, per taxonomy)
  //   callers via POST /api/qa/deliveries/:id/decision.
  // senior_decision / senior_resolution — set by senior/admin callers via
  //   the same endpoint (admin = senior for all decision purposes).
  // effective_decision — PostgreSQL GENERATED ALWAYS AS (STORED) column;
  //   derivation rule: senior wins over agent. Read by Drizzle as a plain
  //   text column; the generated constraint is DB-only and Drizzle never
  //   writes to this column (no ORM INSERT/UPDATE includes it).
  agentDecision: text("agent_decision"),
  agentResolution: text("agent_resolution"),
  seniorDecision: text("senior_decision"),
  seniorResolution: text("senior_resolution"),
  // SPEC-SeniorListview — senior-only listview skip. Global across all seniors.
  // Never affects agent queue. Cleared when a senior decision is made.
  seniorListviewSkippedAt: timestamp("senior_listview_skipped_at"),
  // SPEC-SeniorList-ReadPath-Denormalization — precomputed attention flag.
  // Values: 'mismatch' | 'no_match' | 'first_time' | 'all_match'. NULL = not yet
  // computed (COALESCE to 'first_time' on read). Recomputed at bounded write sites
  // via refreshAttentionFlag (server/lib/attention-flag.ts).
  attentionFlag: text("attention_flag"),
  // GENERATED ALWAYS AS column — Drizzle reads it like a normal column but
  // never writes to it. Declared with generatedAlwaysAs so drizzle-kit
  // recognises the stored-generated nature and doesn't try to DROP+recreate.
  effectiveDecision: text("effective_decision").generatedAlwaysAs(
    sql`CASE WHEN senior_decision IS NOT NULL THEN senior_decision WHEN agent_decision IS NOT NULL THEN agent_decision ELSE NULL END`,
    { mode: "stored" },
  ),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
  // SPEC-1 senior-prefs D7 — CookUnity order barcode = btrim(task_data->>'notes'),
  // kept in sync by storage.createQaDelivery / upsertQaDelivery (the single
  // choke point) and backfilled by the boot migration. Phase 2 all-days search
  // reads this column instead of the jsonb (16 s → sub-second).
  barcode: text("barcode"),
  // LightGlue scene similarity (migrations/2026-10-07-scene-similarity.sql).
  // Created in v0 and written by NOTHING yet: the shadow run keeps every
  // result in scene_similarity_jobs. Workstream 3 (list column + detail
  // card) starts writing them. The scene_ prefix keeps them out of agent
  // payloads via server/lib/agent-payload-filter.ts.
  sceneSimilarityScore: integer("scene_similarity_score"),
  sceneSimilarity: jsonb("scene_similarity"),
}, (table) => [
  index("qa_deliveries_barcode_idx").on(table.barcode),
  // SPEC-SeniorList-ReadPath-Denormalization — partial index backing the
  // senior list read path. Created via migrations/2026-06-14-senior-list-readpath.sql.
  // Declared here so drizzle-kit push recognises it as intended schema and does
  // NOT treat the psql-applied index as drift and drop it.
  index("idx_qa_deliveries_completed_state")
    .on(table.completedAt.desc())
    .where(sql`onfleet_state = 3`),
]);

// Soft-delete record for an individual PDR reference photo. The URL stays in
// training_photo_urls (retained for AI re-extraction and audit); an entry here
// only hides it from the qa-detail-v3 "Verified Reference Photos" display grid.
export interface HiddenTrainingPhoto {
  url: string;        // exact URL string as stored in training_photo_urls
  hiddenAt: string;   // ISO 8601 timestamp
  hiddenBy: string;   // QA-flow user id (UUID) of the agent who hid it
}

// SPEC-PreScreening Phase 2 (WI-1) — shared jsonb shapes for the three Phase 2
// columns. Defined here as the single source of truth; the pure helpers in
// server/prescreening-helpers.ts import these so the logic and the schema can
// never drift.
//
// PrescreenFlag (Decision B) — an advisory validation flag with attribution.
// Free-text, no acknowledgement half (flags are display-only at completion).
export interface PrescreenFlag {
  text: string;
  author: string;
  at: string; // ISO 8601
}
// PrescreenCorrectedField (Decision A) — per editable expected field: the
// model's original prediction (`model`, snapshotted at draft creation per
// Decision C) and the human's correction (`corrected`, null until edited).
export interface PrescreenCorrectedField {
  model: string | null;
  corrected: string | null;
}
export type PrescreenCorrectedFields = Record<string, PrescreenCorrectedField>;
// PdrAgentNote — an address-level history note, attributed, stored newest-first.
// QA detail redesign SPEC-3: shown pinned at the top of Activity. `id` (uuid)
// lets Senior/Admin edit or unpin one note; entries written before SPEC-3 got
// theirs from script/backfill-pdr-note-ids.mjs, and readers treat a missing id
// as read-only. jsonb column, so no DDL.
export interface PdrAgentNote {
  id?: string;
  note: string;
  author: string;
  at: string; // ISO 8601
  editedBy?: string;
  editedAt?: string; // ISO 8601
}
// SPEC-4 senior-prefs — one cap for the note text, enforced by
// buildPdrAgentNote (400) and by the Address notes composer (maxLength).
export const PDR_AGENT_NOTE_MAX_CHARS = 2000;

// SPEC-qa-detail-v3-flags-notes — the two delivery-scoped, QA-side shapes for
// the post-delivery QA view. Distinct from the prescreen page's PrescreenFlag
// (which is advisory-only, no resolve half) and from the recipient-supplied,
// read-only Destination Notes. Defined here as the single source of truth; the
// pure helpers in server/qa-flags-notes-helpers.ts and the client derivations
// in client/src/lib/qa-detail-v3-helpers.ts both import these so logic and
// schema can never drift.
//
// QaFlag — a per-delivery validation alert an agent can raise and later
// resolve. Stored append-order; the UI partitions open vs. resolved on read.
export interface QaFlag {
  id: string;
  text: string;
  author: string;        // agent display name
  createdAt: string;     // ISO 8601
  status: "open" | "resolved";
  resolvedBy?: string;
  resolvedAt?: string;   // ISO 8601
}
// QaNote — a free-form, per-delivery reference note. NOT part of the Good/Bad
// verdict. Stored newest-first; append-only for v1 (no edit/delete).
export interface QaNote {
  id: string;
  text: string;
  author: string;
  createdAt: string;     // ISO 8601
}

// Past Delivery Repository (PDR) - stores verified delivery references for AI training
// Only for dropoff tasks (type=1), unique by company + recipient + street
export const pdrReferences = pgTable("pdr_references", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  company: text("company").notNull(), // From metadata for regular tasks, "CookUnity" for connect tasks
  recipient: text("recipient").notNull(), // From recipients[0].name
  street: text("street").notNull(), // From destination.address.street
  // Additional address details for context
  streetNumber: text("street_number"),
  apartment: text("apartment"),
  city: text("city"),
  state: text("state"),
  postalCode: text("postal_code"),
  // Link to the first verified delivery that created this reference
  firstVerifiedDeliveryId: varchar("first_verified_delivery_id").references(() => qaDeliveries.id),
  // Count of verified deliveries for this reference
  verifiedCount: integer("verified_count").default(0).notNull(),
  // Count of total completed deliveries for this company+recipient+street combo
  recordCount: integer("record_count").default(0).notNull(),
  // AI-learned characteristics for address and drop zone verification
  addressCharacteristics: jsonb("address_characteristics").$type<string[]>(),
  dropZoneCharacteristics: jsonb("drop_zone_characteristics").$type<string[]>(),
  // Enhanced AI signals - OCR anchors (expected text visible at location)
  addressOcrAnchors: jsonb("address_ocr_anchors").$type<string[]>(),
  dropZoneOcrAnchors: jsonb("drop_zone_ocr_anchors").$type<string[]>(),
  // Enhanced AI signals - Visual signature (structured fingerprint for similarity matching)
  addressVisualSignature: jsonb("address_visual_signature").$type<string[]>(),
  dropZoneVisualSignature: jsonb("drop_zone_visual_signature").$type<string[]>(),
  // Enhanced AI signals - Expected objects at this location
  addressObjects: jsonb("address_objects").$type<string[]>(),
  dropZoneObjects: jsonb("drop_zone_objects").$type<string[]>(),
  // QA-verified field values from confirmed deliveries (used by expected extraction)
  verifiedAddressType: text("verified_address_type"),
  verifiedDzeCode: text("verified_dze_code"), // Verified drop zone code (DropZoneCode)
  recipientAliases: text("recipient_aliases").array(),
  trainingPhotoUrls: text("training_photo_urls").array(),
  // Soft-deleted reference photos. URLs remain in training_photo_urls (still fed
  // to AI re-extraction); an entry here only hides the photo from the display grid.
  hiddenTrainingPhotos: jsonb("hidden_training_photos").$type<HiddenTrainingPhoto[]>(),
  // SPEC-PreScreening Phase 2 (WI-1) — address-level history notes, attributed,
  // stored newest-first. Appended by appendPdrAgentNote; shown on every future
  // delivery to this company+recipient+street (prescreen and normal QA).
  agentNotes: jsonb("agent_notes").$type<PdrAgentNote[]>(),
  // Scene fingerprint — structured visual description from the most recent verified delivery's qualifying photo
  sceneFingerprint: jsonb("scene_fingerprint"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

// AI Training Queue - stores deliveries needing human review for AI training
// Three sources: (1) AI vs user disagreements, (2) human inference (no evidence), (3) correct with modifications
// AI Processing Queue - stores background jobs for AI tasks
// Job types: extract_characteristics, predict_delivery
export const aiProcessingQueue = pgTable("ai_processing_queue", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  qaDeliveryId: varchar("qa_delivery_id").notNull().references(() => qaDeliveries.id),
  pdrReferenceId: varchar("pdr_reference_id"),
  // Job type: extract_characteristics, predict_delivery, extract_company_packaging, predict_company_packaging
  jobType: text("job_type").notNull().default("extract_characteristics"),
  // Data needed for AI processing
  photoUrls: text("photo_urls").array().notNull(),
  address: text("address").notNull(),
  dropZoneTag: text("drop_zone_tag").notNull(),
  // For predict_delivery jobs - store characteristics to compare against
  addressCharacteristics: text("address_characteristics").array(),
  dropZoneCharacteristics: text("drop_zone_characteristics").array(),
  // Enhanced AI signals - baseline data for prediction comparison
  addressOcrAnchors: text("address_ocr_anchors").array(),
  dropZoneOcrAnchors: text("drop_zone_ocr_anchors").array(),
  addressVisualSignature: text("address_visual_signature").array(),
  dropZoneVisualSignature: text("drop_zone_visual_signature").array(),
  addressObjects: text("address_objects").array(),
  dropZoneObjects: text("drop_zone_objects").array(),
  // For company packaging jobs (Type 2/3)
  companyPdrId: varchar("company_pdr_id"),
  companyName: text("company_name"),
  packagingCharacteristics: text("packaging_characteristics").array(),
  labelingCharacteristics: text("labeling_characteristics").array(),
  expectedRecipientName: text("expected_recipient_name"),
  labelNameMatch: text("label_name_match"),
  // Enhanced AI signals - company baseline data
  labelOcrAnchors: text("label_ocr_anchors").array(),
  packagingVisualSignature: text("packaging_visual_signature").array(),
  packagingObjects: text("packaging_objects").array(),
  labelObjects: text("label_objects").array(),
  // Job status: pending, processing, completed, failed
  status: text("status").notNull().default("pending"),
  // Error tracking for failed jobs
  errorMessage: text("error_message"),
  retryCount: integer("retry_count").default(0).notNull(),
  // Timestamps
  createdAt: timestamp("created_at").defaultNow().notNull(),
  startedAt: timestamp("started_at"),
  completedAt: timestamp("completed_at"),
});

// Training Criterion State - 7-state cycle for detailed AI Model Priming decisions
export const TRAINING_CRITERION_STATES = {
  GOOD: "good",
  WRONG: "wrong",
  UNCLEAR: "unclear",
  GOOD_INFERENCE: "good_inference",
  BAD_INFERENCE: "bad_inference",
  UNCLEAR_INFERENCE: "unclear_inference",
  GOOD_UPDATE: "good_update",
} as const;

export type TrainingCriterionState = typeof TRAINING_CRITERION_STATES[keyof typeof TRAINING_CRITERION_STATES] | null;

export const TRAINING_CRITERION_LABELS: Record<string, string> = {
  good: "Good",
  wrong: "Wrong",
  unclear: "?",
  good_inference: "Good (inference)",
  bad_inference: "Bad (inference)",
  unclear_inference: "? (inference)",
  good_update: "Good with update",
};

export const TRAINING_CRITERION_CYCLE: (TrainingCriterionState)[] = [
  null, "good", "good_update", "unclear", "wrong", "good_inference", "bad_inference", "unclear_inference"
];

export const PACKAGING_TAG_OPTIONS = [
  "Brown Bag",
  "White Bag",
  "Plastic Bag",
  "Cardboard Box",
  "Branded Box",
  "Tote Bag",
  "Cooler Bag",
  "Shrink Wrap",
  "Crate",
  "Other",
] as const;

export const LABELING_TAG_OPTIONS = [
  "Printed Label",
  "Handwritten",
  "Thermal Sticker",
  "Card Attached",
  "Marker on Bag",
  "Taped Note",
  "No Label",
  "Digital Label",
  "Other",
] as const;

// Company Packaging PDR - stores company-specific packaging/label characteristics for Type 2/3
// Key is Company name only - learns packaging patterns per company
// Label name match options for company training
export const LABEL_NAME_MATCH_OPTIONS = {
  EXACT: "exact",
  PARTIAL: "partial", 
  BUSINESS_ONLY: "business_only",
  NO_NAME: "no_name",
  DIFFERENT: "different",
} as const;

export type LabelNameMatch = typeof LABEL_NAME_MATCH_OPTIONS[keyof typeof LABEL_NAME_MATCH_OPTIONS];

export const LABEL_NAME_MATCH_LABELS: Record<LabelNameMatch, string> = {
  exact: "Exact Match - Full name matches delivery recipient",
  partial: "Partial Match - Some portion of name visible",
  business_only: "Business Name Only - Shows business/store name instead",
  no_name: "No Name - Label doesn't include recipient name",
  different: "Different Format - Abbreviated, initials, or alternate spelling",
};

export const RECIPIENT_SOURCE_POLICY_OPTIONS = {
  DEFAULT: "default",
  LABEL_ONLY: "label_only",
  INVOICE_PREFERRED: "invoice_preferred",
  SIGNATURE_PREFERRED: "signature_preferred",
  NOTES_PREFERRED: "notes_preferred",
} as const;

export type RecipientSourcePolicy = typeof RECIPIENT_SOURCE_POLICY_OPTIONS[keyof typeof RECIPIENT_SOURCE_POLICY_OPTIONS];

export const RECIPIENT_SOURCE_POLICY_LABELS: Record<RecipientSourcePolicy, string> = {
  default: "Default — standard precedence (label > invoice > signature > notes)",
  label_only: "Label Only — only accept recipient from attached package label",
  invoice_preferred: "Invoice Preferred — prioritize invoice/packing slip over label",
  signature_preferred: "Signature Preferred — prioritize signature text over OCR",
  notes_preferred: "Notes Preferred — prioritize completion notes over OCR/signature",
};

export interface ItemProfileEntry {
  description: string;
  formFactor?: string;
  material?: string;
  color?: string;
  brandedMarks?: string[];
}

export const companyPackagingPdr = pgTable("company_packaging_pdr", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  company: text("company").notNull().unique(), // Company name from metadata
  // Packaging characteristics learned from verified deliveries
  packageTypes: jsonb("package_types").$type<string[]>(), // e.g. ["cardboard box", "plastic bag"]
  labelTypes: jsonb("label_types").$type<string[]>(), // e.g. ["card attached", "marker written on plastic"]
  labelPlacement: jsonb("label_placement").$type<string[]>(), // e.g. ["attached to package", "inside bag"]
  // Label name matching pattern - how recipient name appears on labels
  labelNameMatch: text("label_name_match").$type<LabelNameMatch>(), // exact, partial, business_only, no_name, different
  // AI-extracted detailed characteristics for verification
  packagingCharacteristics: jsonb("packaging_characteristics").$type<string[]>(),
  labelingCharacteristics: jsonb("labeling_characteristics").$type<string[]>(),
  // Enhanced AI signals - OCR anchors (expected text on packaging/labels)
  labelOcrAnchors: jsonb("label_ocr_anchors").$type<string[]>(),
  // Enhanced AI signals - Visual signature for packaging similarity matching
  packagingVisualSignature: jsonb("packaging_visual_signature").$type<string[]>(),
  // Enhanced AI signals - Expected objects (packaging components)
  packagingObjects: jsonb("packaging_objects").$type<string[]>(),
  labelObjects: jsonb("label_objects").$type<string[]>(),
  itemProfile: jsonb("item_profile").$type<ItemProfileEntry[]>(),
  recipientSourcePolicy: text("recipient_source_policy").$type<RecipientSourcePolicy>(),
  trainingPhotoUrls: text("training_photo_urls").array(),
  // Embedded images as base64 for faster AI access
  embeddedImages: jsonb("embedded_images").$type<Array<{url: string; base64: string; mimeType: string}>>(),
  // Count of verified deliveries that contributed to training
  verifiedCount: integer("verified_count").default(0).notNull(),
  // Count of bad match samples (for learning what NOT to look for)
  badMatchCount: integer("bad_match_count").default(0).notNull(),
  // Training status: untrained (no samples), trained (has good samples), unverified (only bad samples)
  status: text("status").notNull().default("untrained"), // untrained, trained, unverified
  // Timestamps
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
  lastTrainedAt: timestamp("last_trained_at"),
});

// Company Training Queue - persists imported task IDs for company training
// Allows users to import task IDs and resume training later
export const companyTrainingQueue = pgTable("company_training_queue", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  // Reference to the QA delivery
  qaDeliveryId: varchar("qa_delivery_id").notNull().references(() => qaDeliveries.id),
  onfleetTaskId: text("onfleet_task_id").notNull(), // Full Onfleet task ID
  // Company extracted from task metadata
  company: text("company").notNull(),
  // Status: pending (awaiting training), trained (good sample), bad_match (unusable)
  status: text("status").notNull().default("pending"), // pending, trained, bad_match
  // Photos selected for training (subset of delivery photos)
  selectedPhotoUrls: text("selected_photo_urls").array(),
  // Label name match pattern selected by user
  labelNameMatch: text("label_name_match").$type<LabelNameMatch>(),
  // If marked as bad_match, reason why
  badMatchReason: text("bad_match_reason"),
  // Free-text reasoning notes from trainer explaining thought process and inferences
  trainingNotes: text("training_notes"),
  // 7-state training criteria decisions
  packagingState: text("packaging_state").$type<TrainingCriterionState>(),
  labelingState: text("labeling_state").$type<TrainingCriterionState>(),
  recipientState: text("recipient_state").$type<TrainingCriterionState>(),
  // Tags selected during training review
  packagingTags: text("packaging_tags").array(),
  labelingTags: text("labeling_tags").array(),
  // Whether this sample was saved as a reference
  saveAsReference: boolean("save_as_reference"),
  // Timestamps
  createdAt: timestamp("created_at").defaultNow().notNull(),
  completedAt: timestamp("completed_at"),
});

// Location & Drop Zone Priming - general knowledge base for AI
export const LOCATION_PRIMING_CATEGORIES = [
  "Location Type",
  "Drop Zone",
  "Address Pattern",
  "Building Feature",
  "Access Point",
] as const;

export type LocationPrimingCategory = typeof LOCATION_PRIMING_CATEGORIES[number];

export const locationPrimingEntries = pgTable("location_priming_entries", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  category: text("category").$type<LocationPrimingCategory>().notNull(),
  title: text("title").notNull(),
  description: text("description"),
  notes: text("notes"),
  photoUrls: text("photo_urls").array(),
  tags: text("tags").array(),
  createdBy: text("created_by"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

// Verification sessions - stores test results
export const verificationSessions = pgTable("verification_sessions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  customerId: varchar("customer_id").notNull().references(() => customers.id, { onDelete: "cascade" }),
  testImageUrls: text("test_image_urls").array().notNull(),
  aiAddressResult: boolean("ai_address_result"),
  aiDropzoneResult: boolean("ai_dropzone_result"),
  aiAddressConfidence: integer("ai_address_confidence"), // 0-100
  aiDropzoneConfidence: integer("ai_dropzone_confidence"), // 0-100
  userAddressResult: boolean("user_address_result"),
  userDropzoneResult: boolean("user_dropzone_result"),
  matchedCriteria: jsonb("matched_criteria").$type<string[]>(),
  unmatchedCriteria: jsonb("unmatched_criteria").$type<string[]>(),
  userFeedback: text("user_feedback"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

// Bulk Import Queue - stores raw task IDs for background processing
export const bulkImportQueue = pgTable("bulk_import_queue", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  taskId: text("task_id").notNull(),
  status: text("status").notNull().default("pending"), // pending, processing, completed, failed, duplicate
  errorMessage: text("error_message"),
  batchId: text("batch_id").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  processedAt: timestamp("processed_at"),
});

// Bulk Import Batches - tracks overall progress of a bulk import
export const bulkImportBatches = pgTable("bulk_import_batches", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  totalCount: integer("total_count").notNull(),
  pendingCount: integer("pending_count").notNull(),
  completedCount: integer("completed_count").notNull().default(0),
  failedCount: integer("failed_count").notNull().default(0),
  duplicateCount: integer("duplicate_count").notNull().default(0),
  predictionsQueued: integer("predictions_queued").notNull().default(0),
  status: text("status").notNull().default("pending"), // pending, processing, completed
  source: text("source").default("sync"), // "sync" = regular import, "pdr_import" = PDR discovery import
  createdAt: timestamp("created_at").defaultNow().notNull(),
  completedAt: timestamp("completed_at"),
});

// Insert schemas
export const insertCustomerSchema = createInsertSchema(customers).omit({ id: true, createdAt: true });
export const insertCharacteristicSchema = createInsertSchema(characteristics).omit({ id: true });
export const insertTrainingPhotoSchema = createInsertSchema(trainingPhotos).omit({ id: true, createdAt: true });
export const insertVerificationSessionSchema = createInsertSchema(verificationSessions).omit({ id: true, createdAt: true });
export const insertQaDeliverySchema = createInsertSchema(qaDeliveries).omit({ id: true, createdAt: true, updatedAt: true });
export const insertPdrReferenceSchema = createInsertSchema(pdrReferences).omit({ id: true, createdAt: true, updatedAt: true });
export const insertAiProcessingQueueSchema = createInsertSchema(aiProcessingQueue).omit({ id: true, createdAt: true, startedAt: true, completedAt: true });
export const insertCompanyPackagingPdrSchema = createInsertSchema(companyPackagingPdr).omit({ id: true, createdAt: true, updatedAt: true, lastTrainedAt: true });
export const insertCompanyTrainingQueueSchema = createInsertSchema(companyTrainingQueue).omit({ id: true, createdAt: true, completedAt: true });
export const insertLocationPrimingEntrySchema = createInsertSchema(locationPrimingEntries).omit({ id: true, createdAt: true, updatedAt: true });
export const insertBulkImportQueueSchema = createInsertSchema(bulkImportQueue).omit({ id: true, createdAt: true, processedAt: true });
export const insertBulkImportBatchSchema = createInsertSchema(bulkImportBatches).omit({ id: true, createdAt: true, completedAt: true });

// Types
export type InsertCustomer = z.infer<typeof insertCustomerSchema>;
export type Customer = typeof customers.$inferSelect;

export type InsertCharacteristic = z.infer<typeof insertCharacteristicSchema>;
export type Characteristic = typeof characteristics.$inferSelect;

export type InsertTrainingPhoto = z.infer<typeof insertTrainingPhotoSchema>;
export type TrainingPhoto = typeof trainingPhotos.$inferSelect;

export type InsertVerificationSession = z.infer<typeof insertVerificationSessionSchema>;
export type VerificationSession = typeof verificationSessions.$inferSelect;

export type InsertQaDelivery = z.infer<typeof insertQaDeliverySchema>;
export type QaDelivery = typeof qaDeliveries.$inferSelect;

export type InsertPdrReference = z.infer<typeof insertPdrReferenceSchema>;
export type PdrReference = typeof pdrReferences.$inferSelect;

export type InsertAiProcessingQueue = z.infer<typeof insertAiProcessingQueueSchema>;
export type AiProcessingQueue = typeof aiProcessingQueue.$inferSelect;

export type InsertCompanyPackagingPdr = z.infer<typeof insertCompanyPackagingPdrSchema>;
export type CompanyPackagingPdr = typeof companyPackagingPdr.$inferSelect;

export type InsertCompanyTrainingQueue = z.infer<typeof insertCompanyTrainingQueueSchema>;
export type CompanyTrainingQueue = typeof companyTrainingQueue.$inferSelect;

export type InsertLocationPrimingEntry = z.infer<typeof insertLocationPrimingEntrySchema>;
export type LocationPrimingEntry = typeof locationPrimingEntries.$inferSelect;

export type InsertBulkImportQueue = z.infer<typeof insertBulkImportQueueSchema>;
export type BulkImportQueue = typeof bulkImportQueue.$inferSelect;

export type InsertBulkImportBatch = z.infer<typeof insertBulkImportBatchSchema>;
export type BulkImportBatch = typeof bulkImportBatches.$inferSelect;

// Agent Expected Labels (Gold Data) — human-confirmed expected values
// One row per delivery, upserted on QA submit
export const agentExpectedLabels = pgTable("agent_expected_labels", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  qaDeliveryId: varchar("qa_delivery_id").notNull().references(() => qaDeliveries.id, { onDelete: "cascade" }).unique(),
  recipient: text("recipient"),
  streetNumber: text("street_number"),
  unitNumber: text("unit_number"),
  addressType: text("address_type"),
  dzeCode: text("dze_code"), // DropZoneCode
  qatt: text("qatt"),
  otherCompletionRequirements: jsonb("other_completion_requirements").$type<string[]>(),
  agentUserId: varchar("agent_user_id").notNull(),
  labeledAt: timestamp("labeled_at").defaultNow().notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => [
  index("idx_agent_expected_labels_delivery").on(table.qaDeliveryId),
  index("idx_agent_expected_labels_agent").on(table.agentUserId),
]);

export const insertAgentExpectedLabelSchema = createInsertSchema(agentExpectedLabels).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertAgentExpectedLabel = z.infer<typeof insertAgentExpectedLabelSchema>;
export type AgentExpectedLabel = typeof agentExpectedLabels.$inferSelect;

// Agent Actual Labels (Gold Data) — human-confirmed actual (POD-observed) values
// One row per delivery, upserted on QA submit
export const agentActualLabels = pgTable("agent_actual_labels", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  qaDeliveryId: varchar("qa_delivery_id").notNull().references(() => qaDeliveries.id, { onDelete: "cascade" }).unique(),
  recipient: text("recipient"),
  streetNumber: text("street_number"),
  unitNumber: text("unit_number"),
  addressType: text("address_type"),
  dzeCode: text("dze_code"), // DropZoneCode
  otherCompletionRequirements: jsonb("other_completion_requirements").$type<string[]>(),
  agentUserId: varchar("agent_user_id").notNull(),
  labeledAt: timestamp("labeled_at").defaultNow().notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => [
  index("idx_agent_actual_labels_delivery").on(table.qaDeliveryId),
  index("idx_agent_actual_labels_agent").on(table.agentUserId),
]);

export const insertAgentActualLabelSchema = createInsertSchema(agentActualLabels).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertAgentActualLabel = z.infer<typeof insertAgentActualLabelSchema>;
export type AgentActualLabel = typeof agentActualLabels.$inferSelect;

// Training Queue Items — created when model prediction disagrees with agent label
// NOT created when agentExpected ≠ agentActual (that's a delivery issue, not a model issue)
export const trainingQueueItems = pgTable("training_queue_items", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  qaDeliveryId: varchar("qa_delivery_id").notNull().references(() => qaDeliveries.id, { onDelete: "cascade" }),
  field: text("field").notNull(),
  side: text("side").notNull(),
  modelValue: text("model_value"),
  agentValue: text("agent_value"),
  overrideContextJson: jsonb("override_context_json").$type<{
    structuredOverridesApplied: Array<{ field: string; value: string; side: string }>;
    overrideReason?: string | null;
    evidenceRefs?: Array<{ evidenceType?: string; description?: string; photoRef?: string }>;
    qaModifiedCriteria?: string[];
  }>(),
  status: text("status").notNull().default("pending"),
  reviewedBy: varchar("reviewed_by"),
  reviewedAt: timestamp("reviewed_at"),
  reviewNotes: text("review_notes"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => [
  index("idx_training_queue_delivery").on(table.qaDeliveryId),
  index("idx_training_queue_status").on(table.status),
  index("idx_training_queue_field_side").on(table.field, table.side),
]);

export const insertTrainingQueueItemSchema = createInsertSchema(trainingQueueItems).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertTrainingQueueItem = z.infer<typeof insertTrainingQueueItemSchema>;
export type TrainingQueueItem = typeof trainingQueueItems.$inferSelect;

// Model Audit Findings — Sarah's per-delivery model grading from Audit Mode
// (?audit=1 on qa-detail-v3). One row per delivery, upserted. Deliberately
// separate from agent_*_labels / model_measurements / training_queue_items:
// those are written by the QA decision flow; this table is written ONLY by
// the /api/admin/model-audit router and never feeds training or decisions.
// Verdict/field/side vocabulary lives in shared/model-audit.ts.
export const modelAuditFindings = pgTable("model_audit_findings", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  qaDeliveryId: varchar("qa_delivery_id").notNull().references(() => qaDeliveries.id, { onDelete: "cascade" }).unique(),
  // { expected: {streetNumber, unitNumber, dzeCode}, actual: {...} } →
  // each cell one of: correct | wrong | missed | not_present
  verdicts: jsonb("verdicts").$type<Record<string, Record<string, string>>>().notNull(),
  // Model values at grading time (same shape, string|null cells) — the
  // pipeline gets re-run, so the finding keeps what the model said then.
  modelSnapshot: jsonb("model_snapshot").$type<Record<string, Record<string, string | null>>>(),
  // Compact provenance labels per cell (same shape) — "PDR", "BERT 93%",
  // "scene 85%", ... — so summaries can split error rates by pipeline stage.
  sources: jsonb("sources").$type<Record<string, Record<string, string | null>>>(),
  note: text("note"),
  auditorId: varchar("auditor_id").notNull(),
  auditorName: text("auditor_name"),
  auditedAt: timestamp("audited_at").defaultNow().notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => [
  index("idx_model_audit_findings_delivery").on(table.qaDeliveryId),
]);

export type ModelAuditFinding = typeof modelAuditFindings.$inferSelect;

// ── LightGlue scene similarity (v0 shadow run) ──────────────────────────────
// migrations/2026-10-07-scene-similarity.sql; plan ~/.claude/plans/im-ready-to-
// integrate-staged-tower.md §2.3; audit docs/specs/scene-similarity/AUDIT-2026-
// 10-07-v0-shadow.md. Work queue leased by the GCP worker (pull model). One row
// per (delivery, kind, evidence hash, engine version): a new photo set or a new
// scoring engine is a new job; an identical one is deduped by the unique index.
export const sceneSimilarityJobs = pgTable("scene_similarity_jobs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  deliveryId: varchar("delivery_id").notNull().references(() => qaDeliveries.id, { onDelete: "cascade" }),
  kind: text("kind").notNull(),
  evidenceHash: text("evidence_hash").notNull().default(""),
  engineVersion: text("engine_version").notNull(),
  status: text("status").notNull().default("pending"),
  outcome: text("outcome"),
  leaseUntil: timestamp("lease_until"),
  leaseToken: text("lease_token"),
  leasedBy: text("leased_by"),
  notBefore: timestamp("not_before"),
  attempts: integer("attempts").notNull().default(0),
  score: integer("score"),
  result: jsonb("result"),
  error: text("error"),
  supersededAtResult: boolean("superseded_at_result").notNull().default(false),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  leasedAt: timestamp("leased_at"),
  finishedAt: timestamp("finished_at"),
}, (table) => [
  uniqueIndex("scene_similarity_jobs_key").on(table.deliveryId, table.kind, table.evidenceHash, table.engineVersion),
  index("idx_scene_similarity_jobs_pending").on(table.createdAt).where(sql`status = 'pending'`),
  index("idx_scene_similarity_jobs_delivery").on(table.deliveryId),
  index("idx_scene_similarity_jobs_finished").on(table.finishedAt),
]);

export type SceneSimilarityJob = typeof sceneSimilarityJobs.$inferSelect;

// Reference photos per door (address door key, shared/scene-door-key.ts). One
// row per (delivery, photo) so the continuity method can add non-anchor photos
// and view ids without a second migration. Statuses: plan §2.1 table.
export const sceneReferencePhotos = pgTable("scene_reference_photos", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  doorKey: text("door_key").notNull(),
  streetNumber: integer("street_number"),
  street: text("street"),
  fsa: text("fsa"),
  unitNorm: text("unit_norm").notNull().default(""),
  deliveryId: varchar("delivery_id").notNull().references(() => qaDeliveries.id, { onDelete: "cascade" }),
  photoId: text("photo_id").notNull(),
  bagBox: jsonb("bag_box").$type<[number, number, number, number] | null>(),
  isAnchor: boolean("is_anchor").notNull().default(true),
  viewId: text("view_id"),
  evidenceHash: text("evidence_hash"),
  status: text("status").notNull().default("awaiting_admission"),
  admittedHow: text("admitted_how"),
  refVsRefBest: integer("ref_vs_ref_best"),
  keypointsOutsideBag: integer("keypoints_outside_bag"),
  completedAt: timestamp("completed_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  revokedAt: timestamp("revoked_at"),
  revokedReason: text("revoked_reason"),
  hiddenBy: varchar("hidden_by"),
}, (table) => [
  uniqueIndex("scene_reference_photos_delivery_photo").on(table.deliveryId, table.photoId),
  index("idx_scene_reference_photos_door_active").on(table.doorKey, table.completedAt.desc()).where(sql`status = 'active'`),
  index("idx_scene_reference_photos_delivery").on(table.deliveryId),
]);

export type SceneReferencePhoto = typeof sceneReferencePhotos.$inferSelect;

// QA Resolution Events — audit trail for every resolution cycle
// One event per resolution cycle on a delivery
export const qaResolutionEvents = pgTable("qa_resolution_events", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  qaDeliveryId: varchar("qa_delivery_id").notNull().references(() => qaDeliveries.id, { onDelete: "cascade" }),
  cycle: integer("cycle").notNull(), // 1-based resolution cycle number
  previousDecision: text("previous_decision"), // qaDecision before this resolution (Good/Bad/Unclear)
  previousStatus: text("previous_status"), // starlingStatus before this resolution
  newDecision: text("new_decision"), // qaDecision after this resolution (Good/Bad/Unclear)
  newStatus: text("new_status"), // starlingStatus after this resolution
  resolvedBy: varchar("resolved_by").notNull(), // User ID who performed the resolution
  resolvedByName: text("resolved_by_name"),
  resolutionNotes: text("resolution_notes"),
  resolutionItems: jsonb("resolution_items"), // Array of {issueType, resolutionMethod, notes}
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_resolution_events_delivery").on(table.qaDeliveryId),
  index("idx_resolution_events_cycle").on(table.qaDeliveryId, table.cycle),
]);

export const insertQaResolutionEventSchema = createInsertSchema(qaResolutionEvents).omit({ id: true, createdAt: true });
export type InsertQaResolutionEvent = z.infer<typeof insertQaResolutionEventSchema>;
export type QaResolutionEvent = typeof qaResolutionEvents.$inferSelect;

// Priming Labels — stores agent confirmations during Actual-model priming
// Separate from normal QA submission; records field-level truth for training
export const primingLabels = pgTable("priming_labels", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  qaDeliveryId: varchar("qa_delivery_id").notNull().references(() => qaDeliveries.id),
  primingType: text("priming_type").notNull().$type<"packaging_labeling" | "drop_zone">(),
  confirmedActual: jsonb("confirmed_actual").$type<{
    recipient?: string | null;
    streetNumber?: string | null;
    unitNumber?: string | null;
    addressType?: string | null;
    dzeCode?: string | null;
    signatureName?: string | null;
  }>(),
  evidenceReviewed: jsonb("evidence_reviewed").$type<Array<{
    photoIndex: number;
    signals: string[];
    notes?: string;
  }>>(),
  evidenceRegions: jsonb("evidence_regions").$type<Array<{
    id: string;
    photoIndex: number;
    regionType: string;
    x: number;
    y: number;
    width: number;
    height: number;
    transcribedText?: string;
    note?: string;
  }>>(),
  notes: text("notes"),
  createdBy: text("created_by").notNull(),
  createdByName: text("created_by_name"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_priming_labels_delivery").on(table.qaDeliveryId),
  index("idx_priming_labels_type").on(table.primingType),
]);

export const insertPrimingLabelSchema = createInsertSchema(primingLabels).omit({ id: true, createdAt: true });
export type InsertPrimingLabel = z.infer<typeof insertPrimingLabelSchema>;
export type PrimingLabel = typeof primingLabels.$inferSelect;

export const primingTrainingQueue = pgTable("priming_training_queue", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  qaDeliveryId: varchar("qa_delivery_id").notNull().references(() => qaDeliveries.id, { onDelete: "cascade" }),
  primingLabelId: varchar("priming_label_id").notNull().references(() => primingLabels.id, { onDelete: "cascade" }),
  primingType: text("priming_type").notNull().$type<"packaging_labeling" | "drop_zone">(),
  payload: jsonb("payload").notNull(),
  source: text("source").notNull().default("priming"),
  status: text("status").notNull().default("pending").$type<"pending" | "reviewed" | "exported">(),
  reviewedBy: varchar("reviewed_by"),
  reviewedAt: timestamp("reviewed_at"),
  reviewNotes: text("review_notes"),
  createdBy: text("created_by").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_priming_training_queue_delivery").on(table.qaDeliveryId),
  index("idx_priming_training_queue_status").on(table.status),
  index("idx_priming_training_queue_type").on(table.primingType),
  index("idx_priming_training_queue_source").on(table.source),
]);

// External integrations (single-row configs like Podium OAuth)
// key examples: "podium"
export const integrations = pgTable("integrations", {
  key: text("key").primaryKey(), // "podium"
  accessToken: text("access_token"),
  refreshToken: text("refresh_token"),
  accessTokenExpiresAt: timestamp("access_token_expires_at"),
  locationUid: text("location_uid"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const insertPrimingTrainingQueueSchema = createInsertSchema(primingTrainingQueue).omit({ id: true, createdAt: true, reviewedAt: true });
export type InsertPrimingTrainingQueue = z.infer<typeof insertPrimingTrainingQueueSchema>;
export type PrimingTrainingQueue = typeof primingTrainingQueue.$inferSelect;

// Model measurement records — captured at verification time (go-forward only)
export const modelMeasurements = pgTable("model_measurements", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  qaDeliveryId: varchar("qa_delivery_id").notNull().references(() => qaDeliveries.id, { onDelete: "cascade" }),
  qatt: integer("qatt").notNull(),
  qaDecision: text("qa_decision").notNull(),
  verifiedAt: timestamp("verified_at").defaultNow().notNull(),
  verifiedBy: text("verified_by"),
  expectedFields: jsonb("expected_fields").$type<Record<string, {
    modelValue: string | null;
    agentValue: string | null;
    result: "correct" | "incorrect" | "unable_to_predict";
  }>>().notNull(),
  actualFields: jsonb("actual_fields").$type<Record<string, {
    modelValue: string | null;
    agentValue: string | null;
    result: "correct" | "incorrect" | "unable_to_predict";
  }>>().notNull(),
  baselineFields: jsonb("baseline_fields").$type<Record<string, {
    modelMatch: boolean | null;
    agentMatch: boolean | null;
  }> | null>(),
  baselineLocationFields: jsonb("baseline_location_fields").$type<Record<string, {
    modelMatch: boolean | null;
    agentMatch: boolean | null;
  }> | null>(),
  locationFields: jsonb("location_fields").$type<Record<string, {
    modelCorrect: boolean | null;
    agentCorrect: boolean | null;
    result: "agree" | "disagree" | "unable_to_predict";
  }> | null>(),
  deliveryDate: timestamp("delivery_date"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_model_measurements_qatt").on(table.qatt),
  index("idx_model_measurements_delivery").on(table.qaDeliveryId),
  index("idx_model_measurements_verified_at").on(table.verifiedAt),
  index("idx_model_measurements_delivery_date").on(table.deliveryDate),
]);

export const insertModelMeasurementSchema = createInsertSchema(modelMeasurements).omit({ id: true, createdAt: true });
export type InsertModelMeasurement = z.infer<typeof insertModelMeasurementSchema>;
export type ModelMeasurement = typeof modelMeasurements.$inferSelect;

export const baselineRuns = pgTable("baseline_runs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull(),
  filterJson: jsonb("filter_json").notNull(),
  selectionMode: text("selection_mode").notNull().default("filter"),
  modelVersion: text("model_version").notNull(),
  rulesVersion: text("rules_version").notNull(),
  createdBy: text("created_by").notNull(),
  status: text("status").notNull().default("draft"),
  itemCount: integer("item_count").default(0).notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const baselineRunItems = pgTable("baseline_run_items", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  baselineRunId: varchar("baseline_run_id").notNull().references(() => baselineRuns.id),
  qaDeliveryId: varchar("qa_delivery_id").notNull().references(() => qaDeliveries.id),
  expectedSnapshotJson: jsonb("expected_snapshot_json").notNull(),
  actualSnapshotJson: jsonb("actual_snapshot_json").notNull(),
  diagnosticsSnapshotJson: jsonb("diagnostics_snapshot_json"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const insertBaselineRunSchema = createInsertSchema(baselineRuns).omit({ id: true, createdAt: true, itemCount: true });
export type InsertBaselineRun = z.infer<typeof insertBaselineRunSchema>;
export type BaselineRun = typeof baselineRuns.$inferSelect;

export const insertBaselineRunItemSchema = createInsertSchema(baselineRunItems).omit({ id: true, createdAt: true });
export type InsertBaselineRunItem = z.infer<typeof insertBaselineRunItemSchema>;
export type BaselineRunItem = typeof baselineRunItems.$inferSelect;

export const correctionEvents = pgTable("correction_events", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  baselineRunId: varchar("baseline_run_id").notNull().references(() => baselineRuns.id),
  qaDeliveryId: varchar("qa_delivery_id").notNull().references(() => qaDeliveries.id),
  fieldName: text("field_name").notNull(),
  oldValue: text("old_value"),
  correctedValue: text("corrected_value").notNull(),
  reasonCode: text("reason_code").notNull(),
  analystNote: text("analyst_note"),
  evidenceJson: jsonb("evidence_json").notNull(),
  createdBy: text("created_by").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const insertCorrectionEventSchema = createInsertSchema(correctionEvents).omit({ id: true, createdAt: true });
export type InsertCorrectionEvent = z.infer<typeof insertCorrectionEventSchema>;
export type CorrectionEvent = typeof correctionEvents.$inferSelect;

export const CORRECTION_FIELD_NAMES = ["recipient", "street_number", "unit_number", "address_type", "dz_primary", "dz_secondary"] as const;
export type CorrectionFieldName = typeof CORRECTION_FIELD_NAMES[number];

export const CORRECTION_REASON_CODES = [
  "ocr_misread",
  "scene_misclassification",
  "wrong_pdr_match",
  "extraction_logic_bug",
  "ambiguous_input",
  "missing_data",
  "model_hallucination",
  "other_with_evidence",
] as const;
export type CorrectionReasonCode = typeof CORRECTION_REASON_CODES[number];

export const rulePatches = pgTable("rule_patches", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull(),
  description: text("description"),
  scopeJson: jsonb("scope_json").notNull(),
  rulesJson: jsonb("rules_json").notNull(),
  status: text("status").notNull().default("draft"),
  version: integer("version").notNull().default(1),
  createdBy: text("created_by").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  activatedAt: timestamp("activated_at"),
});

export const rulePatchChanges = pgTable("rule_patch_changes", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  rulePatchId: varchar("rule_patch_id").notNull().references(() => rulePatches.id),
  changeType: text("change_type").notNull(),
  changePayloadJson: jsonb("change_payload_json").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const insertRulePatchSchema = createInsertSchema(rulePatches).omit({ id: true, createdAt: true, activatedAt: true, version: true });
export type InsertRulePatch = z.infer<typeof insertRulePatchSchema>;
export type RulePatch = typeof rulePatches.$inferSelect;

export const insertRulePatchChangeSchema = createInsertSchema(rulePatchChanges).omit({ id: true, createdAt: true });
export type InsertRulePatchChange = z.infer<typeof insertRulePatchChangeSchema>;
export type RulePatchChange = typeof rulePatchChanges.$inferSelect;

export const RULE_PATCH_STATUSES = ["draft", "active", "retired"] as const;
export type RulePatchStatus = typeof RULE_PATCH_STATUSES[number];

export const RULE_CHANGE_TYPES = [
  "add_rule",
  "update_rule",
  "remove_rule",
  "update_scope",
  "update_metadata",
] as const;
export type RuleChangeType = typeof RULE_CHANGE_TYPES[number];

export const RULE_TYPES = [
  "address_type_guardrail",
  "dz_primary_guardrail",
  "weak_cue_rejection",
  "uncertain_fallback",
] as const;
export type RuleType = typeof RULE_TYPES[number];

export const replayRuns = pgTable("replay_runs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  baselineRunId: varchar("baseline_run_id").notNull().references(() => baselineRuns.id),
  rulePatchId: varchar("rule_patch_id").references(() => rulePatches.id),
  modelVersion: text("model_version").notNull(),
  status: text("status").notNull().default("pending"),
  startedAt: timestamp("started_at"),
  completedAt: timestamp("completed_at"),
  summaryJson: jsonb("summary_json"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const replayRunItems = pgTable("replay_run_items", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  replayRunId: varchar("replay_run_id").notNull().references(() => replayRuns.id),
  qaDeliveryId: varchar("qa_delivery_id").notNull().references(() => qaDeliveries.id),
  replayActualJson: jsonb("replay_actual_json").notNull(),
  replayDiagnosticsJson: jsonb("replay_diagnostics_json"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const insertReplayRunSchema = createInsertSchema(replayRuns).omit({ id: true, createdAt: true, startedAt: true, completedAt: true, summaryJson: true });
export type InsertReplayRun = z.infer<typeof insertReplayRunSchema>;
export type ReplayRun = typeof replayRuns.$inferSelect;

export const insertReplayRunItemSchema = createInsertSchema(replayRunItems).omit({ id: true, createdAt: true });
export type InsertReplayRunItem = z.infer<typeof insertReplayRunItemSchema>;
export type ReplayRunItem = typeof replayRunItems.$inferSelect;

export const REPLAY_RUN_STATUSES = ["pending", "running", "completed", "failed"] as const;
export type ReplayRunStatus = typeof REPLAY_RUN_STATUSES[number];

// SPEC-qa-actions-audit-log — `audit_events` renamed to `qa_actions` and
// expanded into the row-per-action substrate the QA Agent rollout needs.
// Three new nullable columns (`delivery_id`, `user_id`, `broadcast_to_slack`),
// `event_type` renamed to `action_type`, `payload_json` renamed to `payload`.
// The legacy `created_by` text column is preserved untouched (the 6 baseline
// rows hold the string 'unknown'); drop in a later cleanup SPEC once
// `user_id` is populated across all writers.
//
// Note: the table NAME (`qa_actions`) and the Drizzle export name (`qaActions`)
// flipped together. `AUDIT_EVENT_TYPES` is aliased to `QA_ACTION_TYPES` below
// so existing baseline/rule-patch/replay/promotion code keeps compiling.
export const qaActions = pgTable("qa_actions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  actionType: text("action_type").notNull(),
  entityType: text("entity_type").notNull(),
  entityId: varchar("entity_id").notNull(),
  // delivery_id is varchar (not uuid) to match qa_deliveries.id (which is
  // declared `varchar PRIMARY KEY DEFAULT gen_random_uuid()` — the value is
  // a UUID, the column type is varchar). user_id is uuid because users.id is
  // uuid per the foundation SPEC.
  deliveryId: varchar("delivery_id").references((): any => qaDeliveries.id),
  userId: uuid("user_id").references((): any => users.id),
  payload: jsonb("payload").notNull().default(sql`'{}'::jsonb`),
  broadcastToSlack: boolean("broadcast_to_slack"),
  createdBy: text("created_by"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  // SPEC-AgentUI-Backend — bad_reason / initial_action capture on the
  // Bad-decision row. CHECK constraints live at the DB level (migration).
  badReason: text("bad_reason"),
  badReasonOther: text("bad_reason_other"),
  initialAction: text("initial_action"),
  initialActionOther: text("initial_action_other"),
}, (table) => [
  // SPEC-SeniorList-ReadPath-Denormalization — join-column index. Created via
  // migrations/2026-06-14-senior-list-readpath.sql. Declared here so drizzle-kit
  // push does not drop it as drift.
  index("idx_qa_actions_delivery").on(table.deliveryId),
  // migrations/2026-09-17-sunday-hot-path-indexes.sql — per-delivery action
  // lookups by type. Built by hand with CONCURRENTLY; declared here so a
  // drizzle-kit push never treats it as drift. Column order and the DESC
  // must match the migration exactly or push drops and recreates it.
  index("idx_qa_actions_type_delivery_created").on(table.actionType, table.deliveryId, table.createdAt.desc()),
]);

export const insertQaActionSchema = createInsertSchema(qaActions).omit({ id: true, createdAt: true });
export type InsertQaAction = z.infer<typeof insertQaActionSchema>;
export type QaAction = typeof qaActions.$inferSelect;

// Backwards-compatible aliases for the in-flight baseline / rule-patch /
// replay / promotion call sites. These let the existing writers compile
// without code changes; new writers should import `qaActions` /
// `InsertQaAction` / `QaAction` directly.
export const auditEvents = qaActions;
export const insertAuditEventSchema = insertQaActionSchema;
export type InsertAuditEvent = InsertQaAction;
export type AuditEvent = QaAction;

export const QA_ACTION_TYPES = [
  // NEW — emitted by this SPEC.
  "claim",
  "unclaim",
  "decision",
  "override",
  "presence_visit",
  "state_change",
  // RESERVED — schema-ready, wired in follow-up SPECs (Q-claim, Q-secondary).
  "extension_tap",
  "secondary_score",
  // SPEC-AgentUI-Backend — new agent-UI action types.
  "note_added",
  // SPEC-DecisionActivityFeed §4.1 — audit trail for edits/deletes.
  // These are feed-suppressed (not surfaced as ActivityEvent entries);
  // they preserve the prior body/photoUrl in `payload` for the audit log.
  "note_edited",
  "note_deleted",
  "resolve_to_good",
  // Legacy — Bad Final was retired 2026-09-02. No new rows are written, but
  // the value stays listed so historical rows still validate and render.
  "mark_bad_final",
  "notify_customer_on",
  "notify_customer_off",
  // SPEC-AgentList-Hardening — agent ready-state transitions + CU-notification
  // "I sent it" confirmation. `skip` is written directly via tx.insert (it
  // already exists on rows in qa_actions) but listing it here lets appendAction
  // emit the same audit row from any future call site without bypassing the
  // enum validation.
  "agent_ready_on",
  "agent_ready_off",
  "notify_customer_sent",
  "skip",
  // SPEC-DeliveryStateModel §11.2 — Photos-Not-Loading. Smaller penalty than
  // skip: a PNL'd delivery returns to the original agent before a skipped one
  // would (tier 1 vs tier 2 in the soft-penalty queue rule, §11.1).
  "pnl",
  // SPEC-DeliveryStateModel §11.3 — Senior listview Skip disposition. Global
  // across all Seniors; does NOT release any claim and does NOT affect Agent
  // listview. Wired by the future SPEC-SeniorListview.md.
  "senior_listview_skip",
  "senior_listview_unskip",
  // SPEC-RedDotOps §6 — a 🔴 message was posted to the ops Slack channel for a
  // Bad delivery. Written only when Slack returned ok; payload carries the
  // message, channel, Slack ts and who was tagged. broadcast_to_slack = true.
  "red_dot_sent",
  // LEGACY — preserved from the old AUDIT_EVENT_TYPES enum so existing
  // baseline / rule-patch / replay / promotion writers keep working.
  "baseline_created",
  "baseline_frozen",
  "correction_created",
  "rule_patch_created",
  "rule_patch_updated",
  "rule_patch_activated",
  "rule_patch_retired",
  "replay_started",
  "replay_completed",
  "gate_evaluation",
  "promotion",
] as const;
export type QaActionType = typeof QA_ACTION_TYPES[number];

// Backwards-compatible alias so the existing `import { AUDIT_EVENT_TYPES }`
// call sites (notably server/__tests__/replay-runs.test.ts) keep compiling.
export const AUDIT_EVENT_TYPES = QA_ACTION_TYPES;
export type AuditEventType = QaActionType;

// SPEC-qa-actions-audit-log + SPEC-DeliveryStateModel §8.2 / §11 —
// canonical list of payload.reason values for an `unclaim` action row. Free-
// form jsonb at the DB level (no CHECK constraint) so this list is the
// app-level contract. New emission sites should pass a value from this list.
// The translation helper below maps the qa_claims.release_reason ENUM into
// this list so both the qa_claims row and the qa_actions row stay coherent.
export const UNCLAIM_REASONS = [
  "user",               // explicit unclaim by the holder
  "disconnect",         // socket dropped
  "timeout",            // idle-release grace expired
  "took_over",          // another user (typically Senior) claimed over
  // SPEC-DeliveryStateModel §11.1 / §11.2 — Skip / Photos-Not-Loading.
  "skip",
  "pnl",
  // SPEC-DeliveryStateModel §8.2 row 3 — final-decision auto-release.
  // Distinguishes a decision-completion release from a generic 'user' release.
  "decision_complete",
  // SPEC-DeliveryStateModel §5.2 / §16 — Senior explicitly reassigning the
  // claim off the current holder.
  "senior_reassign",
] as const;
export type UnclaimReason = (typeof UNCLAIM_REASONS)[number];

/**
 * Translate a qa_claims.release_reason value (QaClaimReleaseReason — a
 * DB-enforced ENUM on the qa_claims table) into the SPEC's qa_actions
 * payload.reason value (UNCLAIM_REASONS — an app-level enum on the
 * qa_actions.payload jsonb field).
 *
 * The two enums live on different tables and use different vocabularies. The
 * qa_claims side describes *why the claim row was closed* (mechanical:
 * submit_decision / user_released / expired_timer / disconnect / stale_sweep /
 * admin_force_release). The qa_actions.payload.reason describes *the user-
 * facing outcome* per SPEC-DeliveryStateModel (user / disconnect / timeout /
 * took_over / skip / pnl / decision_complete / senior_reassign).
 *
 * Every claim-release call site that writes both rows should use this helper
 * for the payload.reason value so the two stay coherent without future drift.
 */
export function unclaimReasonForClaimRelease(
  reason: QaClaimReleaseReason,
): UnclaimReason {
  switch (reason) {
    case "submit_decision":
      return "decision_complete";
    case "user_released":
      return "user";
    case "expired_timer":
      return "timeout";
    case "disconnect":
      return "disconnect";
    case "stale_sweep":
      return "timeout";
    case "admin_force_release":
      return "senior_reassign";
  }
}

// ─── SPEC-AgentUI-Backend — new constants + table for the agent-UI substrate ───

/**
 * Bad-decision reasons (Step 1 of the SPEC-AgentUI taxonomy). Captured on
 * the Bad-decision `qa_actions` row + as a column on `qa_actions.bad_reason`.
 */
export const BAD_REASONS = ["mistake", "cant_verify", "bag_notes", "damage", "other"] as const;
export type BadReason = typeof BAD_REASONS[number];

/**
 * Initial-action taken when Bad is tapped (Step 2 of the SPEC-AgentUI
 * taxonomy). Captured on the same Bad-decision row.
 */
export const INITIAL_ACTIONS = ["phone", "text", "other"] as const;
export type InitialAction = typeof INITIAL_ACTIONS[number];

/**
 * SPEC-RedDotOps §6 — what the /decision API accepts. "slack" is the Agent's
 * #ops chip. It is NOT a storable value: qa_actions.initial_action has a DB
 * CHECK IN ('phone','text','other'), so the row is stored as 'other' with
 * RED_DOT_STORED_ACTION_OTHER (shared/red-dot.ts). Widening the CHECK is a
 * follow-up that has to be ordered with a Replit publish.
 */
export const INITIAL_ACTIONS_API = [...INITIAL_ACTIONS, "slack"] as const;
export type InitialActionApi = typeof INITIAL_ACTIONS_API[number];

/**
 * QA decision values. The qa_deliveries.qa_decision column is text not enum,
 * so this is the application-layer authoritative list. BAD_FINAL was retired
 * 2026-09-02 — the only bad states are bad-not-resolved and bad-resolved,
 * expressed by agent_resolution / senior_resolution (SPEC-DeliveryStateModel
 * §4.1). A resolved bad writes qa_decision = 'GOOD' here for legacy compat.
 */
export const QA_DECISION_VALUES = ["GOOD", "BAD", "UNCLEAR"] as const;
export type QaDecisionValue = typeof QA_DECISION_VALUES[number];

/**
 * qa_resolution_notes — append-only notes machine for the resolution log
 * (per SPEC-AgentUI-Backend §"New table — qa_resolution_notes"). Each entry
 * carries `delivery_id, author_id, timestamp, body, optional photo_url`.
 * Immutable — no UPDATE / DELETE; the "Undo" action mutates qa_decision on
 * the delivery instead.
 */
export const qaResolutionNotes = pgTable("qa_resolution_notes", {
  id: uuid("id").primaryKey().defaultRandom(),
  // delivery_id is varchar to match qa_deliveries.id pattern (the value is
  // a UUID but the column type is varchar — same as qa_actions.delivery_id).
  deliveryId: varchar("delivery_id").notNull().references((): any => qaDeliveries.id, { onDelete: "cascade" }),
  authorId: uuid("author_id").notNull().references((): any => users.id),
  body: text("body").notNull(),
  photoUrl: text("photo_url"),
  // SPEC-DecisionActivityFeed §4.1 — soft-delete tombstone. Non-null = deleted.
  // Hard-delete is never performed; the /activity feed surfaces deleted:true
  // with body/photoUrl nulled so the panel can render a placeholder.
  deletedAt: timestamp("deleted_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const insertQaResolutionNoteSchema = createInsertSchema(qaResolutionNotes).omit({ id: true, createdAt: true });
export type InsertQaResolutionNote = z.infer<typeof insertQaResolutionNoteSchema>;
export type QaResolutionNote = typeof qaResolutionNotes.$inferSelect;

// ─── SPEC-OverrideNotification — qa_notifications table ──────────────────────
//
// Persists every notification row at write time so Agents who are offline
// at override time still receive the notification on next connect.
// notification_type is plain TEXT (not enum) so future event types
// (force_resolve, claim_takeover) add zero migration overhead.
//
// delivery_id is VARCHAR to match qa_deliveries.id (varchar primary key).
export const qaNotifications = pgTable("qa_notifications", {
  id:               uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  recipientId:      uuid("recipient_id").notNull().references((): any => users.id),
  notificationType: text("notification_type").notNull(),
  deliveryId:       varchar("delivery_id").references((): any => qaDeliveries.id),
  payload:          jsonb("payload").notNull(),
  createdAt:        timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  readAt:           timestamp("read_at", { withTimezone: true }),
  dismissedAt:      timestamp("dismissed_at", { withTimezone: true }),
});

export const insertQaNotificationSchema = createInsertSchema(qaNotifications).omit({ id: true, createdAt: true, readAt: true, dismissedAt: true });
export type InsertQaNotification = z.infer<typeof insertQaNotificationSchema>;
export type QaNotification = typeof qaNotifications.$inferSelect;

// ─── SPEC-q-claim — qa_claims canonical claim model ──────────────────────────
//
// The qa_claims table is the canonical record of who currently holds
// responsibility for each delivery. Multi-claim per user is supported (one
// row per active claim); claim_type distinguishes agent primary claims from
// senior secondary review claims. qa_deliveries.owner_id is maintained as a
// denormalized cache by the insertClaim / releaseClaim storage methods
// (primary-only writes). See ~/Productivity/QAM/qa-agent-rollout/SPEC-q-claim.md.

export const QA_CLAIM_TYPES = ["primary", "secondary"] as const;
export type QaClaimType = (typeof QA_CLAIM_TYPES)[number];

export const QA_CLAIM_RELEASE_REASONS = [
  "submit_decision",     // primary: agent submitted GOOD/BAD/Unclear; secondary: senior submitted score
  "user_released",       // explicit "release" tap
  "expired_timer",       // per-claim setTimeout fired without extend or submit
  "disconnect",          // socket closed mid-claim
  "stale_sweep",         // 10-min safety-net sweep caught it
  "admin_force_release", // Sunny/Sarah manually unlocked
] as const;
export type QaClaimReleaseReason = (typeof QA_CLAIM_RELEASE_REASONS)[number];

// Drizzle table. Mirrors the SQL DDL in
// migrations/2026-05-29-qa-claims.sql. Mounted at boot from server/index.ts
// via the idempotent migration block (same pattern as qa_actions).
//
// Note: claim_type and release_reason use Postgres ENUM types on the DB side
// (`qa_claim_type` and `qa_claim_release_reason`). Drizzle's pg-core text()
// stores the string value; the enum is enforced by the DB CHECK on the
// underlying ENUM column. Using text() here keeps the InsertQaClaim shape
// simple (no Drizzle pgEnum dance) and matches how qa_actions handles its
// own action_type column.
export const qaClaims = pgTable("qa_claims", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  deliveryId: varchar("delivery_id").notNull().references((): any => qaDeliveries.id),
  userId: uuid("user_id").notNull().references((): any => users.id),
  // Denormalized display name of the claimer, captured at claim time so a
  // released / swept claim stays legible without a users join. Nullable
  // (legacy rows are backfilled by migrations/2026-09-02-claim-user-name.sql).
  userName: text("user_name"),
  claimType: text("claim_type").notNull().$type<QaClaimType>(),
  claimedAt: timestamp("claimed_at", { withTimezone: true }).defaultNow().notNull(),
  expiresAt: timestamp("expires_at", { withTimezone: true }).notNull(),
  extendedCount: integer("extended_count").notNull().default(0),
  releasedAt: timestamp("released_at", { withTimezone: true }),
  releaseReason: text("release_reason").$type<QaClaimReleaseReason | null>(),
  // Points back to the qa_actions row that created this claim. Nullable so
  // a best-effort appendAction failure doesn't roll back the claim insert
  // (matches the storage.ts:2298–2309 try/catch pattern around existing
  // claim writers).
  qaActionId: varchar("qa_action_id").references((): any => qaActions.id),
});

export const insertQaClaimSchema = createInsertSchema(qaClaims).omit({
  id: true,
  claimedAt: true,
});
export type InsertQaClaim = z.infer<typeof insertQaClaimSchema>;
export type QaClaim = typeof qaClaims.$inferSelect;

// ─── SPEC-ClaimPrimitive — first-class `claims` table (DSM §8) ───────────────
//
// Replaces the implicit claim management (owner_id / owner_name / owner_user_id
// columns on qa_deliveries) with an explicit, audited, DB-enforced claim table.
// Two claims may coexist per delivery: one 'agent' and one 'senior' (Admin maps
// to 'senior' for all claim purposes — DSM §5.3). The partial unique index
// `claims_delivery_role_active` enforces "at most one active claim per role per
// delivery" at the DB level, making the bug class "forgot to release claim in
// this code path" structurally detectable at test time.
//
// The qa_claims table (timer-based, Phase 1) coexists for now. This table is
// the DSM §8 authoritative layer. Dual-write to qa_deliveries.owner_user_id is
// maintained during the transition; a cleanup SPEC drops it once all read paths
// join `claims` instead.

/** Claim role values — DSM §5.3: Admin maps to 'senior'. */
export const CLAIM_ROLES = ["agent", "senior"] as const;
export type ClaimRole = (typeof CLAIM_ROLES)[number];

/**
 * Map an application user role to a claim_role value (DSM §5.3).
 * Admin = Senior for all claim purposes.
 */
export function toClaimRole(role: string): ClaimRole {
  if (role === "agent") return "agent";
  return "senior"; // 'senior' and 'admin' both map to 'senior'
}

/**
 * Unclaim reasons for the new `claims` table. These are the DSM §8.2 release
 * trigger reasons stored in `claims.release_reason` AND emitted as
 * `qa_actions.payload.reason` on every 'unclaim' action row.
 *
 * Distinct from UNCLAIM_REASONS (the older qa_actions payload enum) — the two
 * sets overlap but this one is the claims-table-authoritative list.
 */
export const CLAIM_UNCLAIM_REASONS = [
  "decision_complete", // final decision auto-release (Good, or Bad-resolved)
  "skip",              // holder used the Skip affordance
  "pnl",               // holder marked Photos-Not-Loading
  "disconnect",        // socket dropped + grace period expired
  "timeout",           // idle-release grace expired (presence:off path)
  "senior_reassign",   // Senior took over another Senior's claim
  "user",              // explicit unclaim via UI
] as const;
export type ClaimUnclaimReason = (typeof CLAIM_UNCLAIM_REASONS)[number];

/**
 * Error thrown when acquireClaim encounters the uniqueness constraint
 * (another user already holds the same-role claim on this delivery).
 * The HTTP layer converts this to 409 with { error: 'claim_conflict', ... }.
 */
export class ClaimConflictError extends Error {
  claimedBy: string;
  claimedAt: Date;

  constructor(claimedBy: string, claimedAt: Date) {
    super(`Delivery already claimed by ${claimedBy}`);
    this.name = "ClaimConflictError";
    this.claimedBy = claimedBy;
    this.claimedAt = claimedAt;
    // Restore prototype chain (required for instanceof checks across TS/JS boundaries).
    Object.setPrototypeOf(this, ClaimConflictError.prototype);
  }
}

/** Drizzle table declaration. Mirrors migrations/2026-06-02-claim-primitive.sql. */
export const claims = pgTable("claims", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  deliveryId: varchar("delivery_id").notNull().references((): any => qaDeliveries.id),
  userId: uuid("user_id").notNull().references((): any => users.id),
  // Denormalized display name of the claimer — see qaClaims.userName.
  userName: text("user_name"),
  claimRole: text("claim_role").notNull().$type<ClaimRole>(),
  claimedAt: timestamp("claimed_at", { withTimezone: true }).defaultNow().notNull(),
  releasedAt: timestamp("released_at", { withTimezone: true }),
  releaseReason: text("release_reason").$type<ClaimUnclaimReason | null>(),
  releasedByUserId: uuid("released_by_user_id").references((): any => users.id),
  payload: jsonb("payload").notNull().default({}),
}, (table) => [
  // SPEC-SeniorList-ReadPath-Denormalization — join-column index. Created via
  // migrations/2026-06-14-senior-list-readpath.sql. Declared here so drizzle-kit
  // push does not drop it as drift.
  index("idx_claims_delivery").on(table.deliveryId),
]);

export const insertClaimSchema = createInsertSchema(claims).omit({
  id: true,
  claimedAt: true,
});
export type InsertClaim = z.infer<typeof insertClaimSchema>;
export type Claim = typeof claims.$inferSelect;

export const PROMOTION_GATE_CONFIG = {
  minAddressTypeImprovement: 2.0,
  maxStreetNumberRegression: -1.0,
  maxUnitNumberRegression: -1.0,
  maxDzePrimaryRegression: -1.0,
  minSupportThreshold: 10,
} as const;

// Evidence item for Actual predictions
export type ActualEvidenceItem = {
  field: string;
  evidenceType: "direct" | "contextual" | "weak";
  description: string;
  photoRef?: string;
  confidence?: number;
};

// Override context stored on training queue items
export type OverrideContext = {
  overrides: Array<{ field: string; value: string; side: string }>;
  reason?: string;
  evidenceRefs?: string[];
};

// API Response types
export const namedCohorts = pgTable("named_cohorts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull(),
  description: text("description"),
  qatt: integer("qatt"),
  company: text("company"),
  createdBy: text("created_by").notNull(),
  status: text("status").notNull().default("active"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const namedCohortItems = pgTable("named_cohort_items", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  namedCohortId: varchar("named_cohort_id").notNull().references(() => namedCohorts.id),
  qaDeliveryId: varchar("qa_delivery_id").notNull().references(() => qaDeliveries.id),
  addedAt: timestamp("added_at").defaultNow().notNull(),
});

export const insertNamedCohortSchema = createInsertSchema(namedCohorts).omit({ id: true, createdAt: true });
export type InsertNamedCohort = z.infer<typeof insertNamedCohortSchema>;
export type NamedCohort = typeof namedCohorts.$inferSelect;

export const insertNamedCohortItemSchema = createInsertSchema(namedCohortItems).omit({ id: true, addedAt: true });
export type InsertNamedCohortItem = z.infer<typeof insertNamedCohortItemSchema>;
export type NamedCohortItem = typeof namedCohortItems.$inferSelect;

// ─── User Notification Preferences ──────────────────────────────────────────
// Session 11 — per-user toast suppression, keyed by auth sub (string) so it
// works for both canonical-users (UUID sub) and legacy Replit-auth without a
// users row. Four boolean prefs matching NotificationPrefs in
// shared/notification-preferences.ts.
export const userNotificationPreferences = pgTable("user_notification_preferences", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userKey: text("user_key").notNull().unique(),
  showAutoAdvance: boolean("show_auto_advance").notNull().default(true),
  showActionConfirmations: boolean("show_action_confirmations").notNull().default(true),
  showOtherActivity: boolean("show_other_activity").notNull().default(true),
  showClaimAlerts: boolean("show_claim_alerts").notNull().default(true),
  // SPEC-1 senior-prefs (2026-09-23) — Senior/Admin behaviour switches +
  // personal saved filters, on the same per-user row (D1). Boot migration in
  // server/boot-migrations.ts adds them with these exact defaults.
  autoAdvance: boolean("auto_advance").notNull().default(true),
  showExpectedActual: boolean("show_expected_actual").notNull().default(false),
  showQueueFooter: boolean("show_queue_footer").notNull().default(false),
  savedFilters: jsonb("saved_filters").$type<SavedFilter[]>().notNull().default(sql`'[]'::jsonb`),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export type UserNotificationPreference = typeof userNotificationPreferences.$inferSelect;

export type AIAnalysisResult = {
  addressCharacteristics: string[];
  dropzoneCharacteristics: string[];
};

export type VerificationResult = {
  addressCorrect: boolean;
  dropzoneCorrect: boolean;
  addressConfidence: number;
  dropzoneConfidence: number;
  matchedCriteria: string[];
  unmatchedCriteria: string[];
};

// ─── App Settings ────────────────────────────────────────────────────────────
// QAM-wide, Admin-owned runtime settings. A tiny key/value store rather than a
// column-per-setting table: these are operational switches that come and go,
// and a jsonb value lets a future setting carry more than a boolean without a
// migration. `updated_by` is nullable so a server-side write (a boot default,
// a script) doesn't need to invent a user.
export const appSettings = pgTable("app_settings", {
  key: text("key").primaryKey(),
  value: jsonb("value").notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
  updatedBy: uuid("updated_by").references(() => users.id),
});

export type AppSetting = typeof appSettings.$inferSelect;

/**
 * Known `app_settings.key` values. Import this instead of typing the string —
 * the key is shared by the storage layer, the Admin API and the tests.
 *
 *   agentsSkipSeniorDecided — when true, Agents are never served (Next up /
 *   auto-advance) and cannot claim a delivery whose `senior_decision` is
 *   non-null, including the row-4 case (senior bad, not yet resolved).
 *   Seniors and Admins are unaffected. Default OFF.
 */
export const APP_SETTING_KEYS = {
  agentsSkipSeniorDecided: "agents_skip_senior_decided",
  // SPEC-staging-hardening §5.7 — { mode: "off"|"ingest"|"full", fullUntil }.
  // Only read on a deployment with LIVE_INGEST_SWITCH set.
  liveIngestMode: "live_ingest_mode",
  // SPEC-RedDotOps §4 — { responders: [{ name, slackUserId, active }] }.
  redDotRoster: "red_dot_roster",
} as const;

// ─── Training Simulator (SPEC-TrainingSimulator §6) ──────────────────────────
// Eight tables, `simulator_` prefix (NOT `training_` — training_photos /
// training-labels.ts are ML model training). Items / attempts / answers have
// different cardinalities, so one table per job; grades are stored raw per
// field (D23) and stamped with the key version they were graded against
// (D22). Mirrored by migrations/2026-09-08-training-simulator.sql and the
// boot block in server/index.ts.

export const simulatorItems = pgTable("simulator_items", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  sourceDeliveryId: varchar("source_delivery_id").references(() => qaDeliveries.id, { onDelete: "set null" }),
  onfleetTaskId: varchar("onfleet_task_id").notNull().unique(),
  onfleetShortId: text("onfleet_short_id"),
  snapshot: jsonb("snapshot").notNull(),
  answerKey: jsonb("answer_key").notNull().default(sql`'{}'::jsonb`),
  eligiblePhases: integer("eligible_phases").array().notNull().default(sql`'{}'`),
  inSet: boolean("in_set").notNull().default(false),
  sequence: integer("sequence"),
  answerKeyVersion: integer("answer_key_version").notNull().default(1),
  status: text("status").notNull().default("draft"), // draft | ready | retired
  photoMissing: boolean("photo_missing").notNull().default(false),
  photoCheckedAt: timestamp("photo_checked_at", { withTimezone: true }),
  addedBy: uuid("added_by").references(() => users.id),
  addedAt: timestamp("added_at", { withTimezone: true }).defaultNow().notNull(),
  updatedBy: uuid("updated_by").references(() => users.id),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
  notes: text("notes"),
});
export type SimulatorItem = typeof simulatorItems.$inferSelect;

export const simulatorQuestions = pgTable("simulator_questions", {
  id: text("id").primaryKey(),
  sort: integer("sort").notNull(),
  enabled: boolean("enabled").notNull().default(true),
  mandatoryPhases: integer("mandatory_phases").array().notNull().default(sql`'{}'`),
  text: jsonb("text").notNull(),
  updatedBy: uuid("updated_by").references(() => users.id),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});
export type SimulatorQuestionRow = typeof simulatorQuestions.$inferSelect;

export const simulatorAttempts = pgTable("simulator_attempts", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: uuid("user_id").notNull().references(() => users.id),
  phase: integer("phase").notNull(),
  attemptNo: integer("attempt_no").notNull(),
  startedAt: timestamp("started_at", { withTimezone: true }).defaultNow().notNull(),
  finishedAt: timestamp("finished_at", { withTimezone: true }),
  initiatedBy: uuid("initiated_by").references(() => users.id),
  status: text("status").notNull().default("in_progress"), // in_progress | complete | abandoned
  summary: jsonb("summary"),
});
export type SimulatorAttempt = typeof simulatorAttempts.$inferSelect;

export const simulatorAnswers = pgTable("simulator_answers", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  attemptId: uuid("attempt_id").notNull().references(() => simulatorAttempts.id, { onDelete: "cascade" }),
  itemId: uuid("item_id").notNull().references(() => simulatorItems.id),
  itemIndex: integer("item_index").notNull(),
  answers: jsonb("answers").notNull().default(sql`'{}'::jsonb`),
  grades: jsonb("grades").notNull().default(sql`'{}'::jsonb`),
  keyVersion: integer("key_version").notNull(),
  regradedAt: timestamp("regraded_at", { withTimezone: true }),
  regradedBy: uuid("regraded_by").references(() => users.id),
  decision: text("decision"), // GOOD | BAD | null (no_decision)
  badReason: text("bad_reason"),
  badReasonOther: text("bad_reason_other"),
  resolutionNotes: text("resolution_notes"),
  openedAt: timestamp("opened_at", { withTimezone: true }),
  decidedAt: timestamp("decided_at", { withTimezone: true }),
  elapsedMs: integer("elapsed_ms"),
  timedOut: boolean("timed_out").notNull().default(false),
});
export type SimulatorAnswer = typeof simulatorAnswers.$inferSelect;

export const simulatorTimeEvents = pgTable("simulator_time_events", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  answerId: uuid("answer_id").notNull().references(() => simulatorAnswers.id, { onDelete: "cascade" }),
  kind: text("kind").notNull(), // extend | timeout
  seconds: integer("seconds"),
  at: timestamp("at", { withTimezone: true }).defaultNow().notNull(),
});

export const simulatorLessons = pgTable("simulator_lessons", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  title: text("title").notNull(),
  bodyMd: text("body_md").notNull(),
  images: jsonb("images").notNull().default(sql`'[]'::jsonb`),
  placement: jsonb("placement").notNull(),
  sort: integer("sort").notNull().default(0),
  enabled: boolean("enabled").notNull().default(true),
  createdBy: uuid("created_by").references(() => users.id),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedBy: uuid("updated_by").references(() => users.id),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});
export type SimulatorLesson = typeof simulatorLessons.$inferSelect;

export const simulatorLessonReads = pgTable("simulator_lesson_reads", {
  userId: uuid("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  lessonId: uuid("lesson_id").notNull().references(() => simulatorLessons.id, { onDelete: "cascade" }),
  readAt: timestamp("read_at", { withTimezone: true }).defaultNow().notNull(),
}, (table) => [primaryKey({ columns: [table.userId, table.lessonId] })]);

export const simulatorCertifications = pgTable("simulator_certifications", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: uuid("user_id").notNull().references(() => users.id),
  attemptId: uuid("attempt_id").notNull().references(() => simulatorAttempts.id),
  result: text("result").notNull(), // pass | fail
  signedBy: uuid("signed_by").references(() => users.id),
  signedAt: timestamp("signed_at", { withTimezone: true }).defaultNow().notNull(),
  note: text("note"),
});

// Stored Onfleet route plans for the route map on the delivery detail page
// (server/lib/route-store.ts, migrations/2026-10-27-route-plans.sql). One row
// per plan: the route's task ids in planned order, plus the few task fields a
// card needs for tasks QAM has not imported (not completed yet). Completions
// are never copied here: they are read from qa_deliveries, which the
// taskCompleted webhook keeps current. Written on the first view of a route,
// by routePlanUpdated (plans starting today) and by the map's "Pull again".
export const routePlans = pgTable("route_plans", {
  id: varchar("id").primaryKey(), // Onfleet route plan id
  workerId: varchar("worker_id"),
  name: text("name"),
  shortId: text("short_id"),
  timezone: text("timezone"),
  state: text("state"), // Onfleet plan state: PENDING | IN_PROGRESS | COMPLETED
  startTime: timestamp("start_time"),
  taskIds: text("task_ids").array().notNull(),
  extraTasks: jsonb("extra_tasks").notNull().default(sql`'{}'::jsonb`), // { [taskId]: slim task } for tasks not in qa_deliveries
  pulledAt: timestamp("pulled_at").notNull(), // last time the plan was read from Onfleet
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export type RoutePlanRow = typeof routePlans.$inferSelect;

// Saved display rotation of a delivery photo (shared/photo-rotation.ts,
// migrations/2026-10-29-photo-rotations.sql). One row per photo, keyed like
// qa_deliveries.photo_urls entries; set by the rotate button on the detail
// page and shown to every later viewer. The photo file itself is never changed.
export const photoRotations = pgTable("photo_rotations", {
  photoKey: text("photo_key").primaryKey(),
  rotation: integer("rotation").notNull(), // degrees clockwise: 0 | 90 | 180 | 270
  updatedBy: uuid("updated_by"), // users.id of whoever turned it last
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export type PhotoRotationRow = typeof photoRotations.$inferSelect;
